import React, { useEffect, useRef, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProofJourneyBar } from './components/proof/ProofJourneyBar';
import { DemoProvider } from './contexts/DemoContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ClarityModeProvider } from './contexts/ClarityModeContext';
import { ClarityProvider } from './contexts/ClarityContext';
import { ScrollToTop } from './components/navigation/ScrollToTop';
import { WayfindingRail } from './components/navigation/WayfindingRail';
import { DemoLinkProvider } from './contexts/DemoLinkContext';
import { UndoProvider } from './contexts/UndoContext';
import { HotkeyProvider } from './contexts/HotkeyContext';
import { LensProvider } from './contexts/LensContext';
import { RoleProvider } from './contexts/RoleContext';
// PHASE H: Full Demo Mode
import { DemoModeProvider } from './contexts/DemoModeContext';
import { InstitutionalModeProvider } from './contexts/InstitutionalModeContext';
// FOUNDER DIRECTIVE: Granullo Glass — Structural Overlay Mode
import { GranulloGlassProvider } from './contexts/GranulloGlassContext';
import { GranulloGlassOverlay } from './components/glass/GranulloGlassOverlay';
// FOUNDER DIRECTIVE: Simulation History Timeline — Decision Memory
import { SimulationHistoryProvider } from './contexts/SimulationHistoryContext';
// FOUNDER DIRECTIVE: Exposure Engine — Actuarial Infrastructure for Decisions
import { ExposureEngineProvider } from './contexts/ExposureEngineContext';
// GRANULLO PHASE 2: Institutional Intelligence Expansion
import { CBDCSimulationProvider } from './contexts/CBDCSimulationContext';
import { AuthorityMatrixProvider } from './contexts/AuthorityMatrixContext';
// GRANULLO PRE-LIVE: Programmable Policy Infrastructure
import { ProgrammablePolicyProvider } from './contexts/ProgrammablePolicyContext';
// GRANULLO PLATFORM EVOLUTION: Program Integrity Core Model
import { ProgramIntegrityProvider } from './contexts/ProgramIntegrityContext';
// GRANULLO PLATFORM EVOLUTION: Phase 2 — System Blueprint & Baseline Signals
import { SystemBlueprintProvider } from './contexts/SystemBlueprintContext';
import { BaselineSignalsProvider } from './contexts/BaselineSignalsContext';
// GRANULLO PLATFORM EVOLUTION: Phase 3 — Decision Matrix Engine
import { DecisionMatrixProvider } from './contexts/DecisionMatrixContext';
// GRANULLO PLATFORM EVOLUTION: Phase 4 — Monitoring, Exposure, Cascade, System Stress
import { MonitoringEngineProvider } from './contexts/MonitoringEngineContext';
import { CascadeEngineProvider } from './contexts/CascadeEngineContext';
import { SystemStressProvider } from './contexts/SystemStressContext';
// GRANULLO PLATFORM EVOLUTION: Phase 5 — Scenario Library Upgrade
import { ScenarioEngineProvider } from './contexts/ScenarioEngineContext';
import { InterventionComparisonProvider } from './contexts/InterventionComparisonContext';
// GRANULLO PLATFORM EVOLUTION: Phase 7 — System Memory & Verified Blueprints
import { SystemMemoryProvider } from './contexts/SystemMemoryContext';
import { VerifiedBlueprintProvider } from './contexts/VerifiedBlueprintContext';
// GRANULLO PLATFORM EVOLUTION: Phase 8 — Commit Economy (Reserve Pool)
import { CommitEconomyProvider } from './contexts/CommitEconomyContext';
// FOUNDER DIRECTIVE: Developer Debug Tooling (dev-only)
// DeveloperDebugToolbar removed — dev-only debug controls purged for pilot
// FOUNDER DIRECTIVE: Global System Status Bar
import { GlobalSystemStatusBar } from './components/common/GlobalSystemStatusBar';
// FOUNDER DIRECTIVE: System Walkthrough for first-time users
// NOTE: SystemWalkthrough removed — orphan import (never rendered)
// FOUNDER DIRECTIVE: World-class entry experience for first-time visitors
// NOTE: GranulloEntryExperience removed — orphan import (never rendered)
import { DemoOverlay } from './components/demo/DemoOverlay';
// DemoLauncher floating variant removed — single canonical demo overlay preserved
import { GuidedViewWrapper } from './components/guided/GuidedViewWrapper';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { DemoBadge } from './components/DemoComponents';
// === LAZY-LOADED PAGES (Code-split for performance) ===
const SimulationsPage = lazy(() => import('./pages/SimulationsPage'));
const GranulloDemoConsole = lazy(() => import('./pages/GranulloDemoConsole'));
const DecisionObservatory = lazy(() => import('./pages/DecisionObservatory'));
const CommitmentSimulator = lazy(() => import('./pages/CommitmentSimulator'));
const ZKCommitmentDemo = lazy(() => import('./pages/ZKCommitmentDemo'));
const SharedSimulationView = lazy(() => import('./pages/SharedSimulationView'));
const EvidenceViewPage = lazy(() => import('./pages/EvidenceViewPage'));
const MomentPage = lazy(() => import('./pages/MomentPage'));
const PublicShareView = lazy(() => import('./pages/PublicShareView'));
const PartnerPortal = lazy(() => import('./pages/PartnerPortal'));
const StripeRailDemo = lazy(() => import('./pages/StripeRailDemo'));
const PartnerLogin = lazy(() => import('./pages/workspace/PartnerLogin'));
const PartnerRegister = lazy(() => import('./pages/workspace/PartnerRegister'));
const WorkspaceShell = lazy(() => import('./pages/workspace/WorkspaceShell'));
import { PartnerWorkspaceProvider } from './contexts/PartnerWorkspaceContext';
const GlobalFailureMap = lazy(() => import('./pages/GlobalFailureMap'));
const GranulloOracle = lazy(() => import('./pages/GranulloOracle'));
const HistoricalTimelinePage = lazy(() => import('./pages/HistoricalTimelinePage'));
const AskIntelligencePage = lazy(() => import('./pages/AskIntelligencePage'));
const DataFabricPage = lazy(() => import('./pages/DataFabricPage'));
const PlaybookPage = lazy(() => import('./pages/PlaybookPage'));
const SignalsPage = lazy(() => import('./pages/SignalsPage'));
const MisusePreventionEstimator = lazy(() => import('./pages/MisusePreventionEstimator'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const PartnersIntegratePage = lazy(() => import('./pages/PartnersIntegratePage'));
import { FirstLoginModal } from './components/onboarding/FirstLoginModal';
import { DemoModeBadge } from './components/guided/DemoModeBadge';
import { SessionRecordingControls } from './components/guided/SessionRecordingControls';
import { CommandPalette } from './components/command/CommandPalette';
import { SessionWarning } from './components/shared/SessionWarning';
// FloatingTourButton removed — decluttered for pilot
// Legacy GranulloLens v1 removed — replaced by LensOrb (lens-v2)
// SYSTEM FABRIC: Universal Intelligence Composer Dock
import AskGranulloDock from './components/intelligence/AskGranulloDock';
// FOUNDER DIRECTIVE: Persistent cognition entrypoints
// FloatingUtilities removed — single LensOrb + AskGranulloDock are canonical entry points
// LENS V2: Universal Interpretability Layer
import { GranulloLensV2Provider, LensOrb, LensPanel } from './components/lens-v2';
// GTM HANDOFF: Partner-facing and founder playbook surfaces
const GTMHub = lazy(() => import('./pages/gtm/GTMHub'));
const RailPartnerNarrative = lazy(() => import('./pages/gtm/RailPartnerNarrative'));
const PartnerOnePager = lazy(() => import('./pages/gtm/PartnerOnePager'));
const SponsorBankLanding = lazy(() => import('./pages/gtm/SponsorBankLanding'));
const RailPartnerLanding = lazy(() => import('./pages/gtm/RailPartnerLanding'));
const FounderDemoChoreography = lazy(() => import('./pages/gtm/FounderDemoChoreography'));
const ObjectionHandlingPage = lazy(() => import('./pages/gtm/ObjectionHandling'));
const CaseStudyScripts = lazy(() => import('./pages/gtm/CaseStudyScripts'));
const WHYProofStoryboards = lazy(() => import('./pages/gtm/WHYProofStoryboards'));
const PilotRolloutNarrative = lazy(() => import('./pages/gtm/PilotRolloutNarrative'));
const ExpansionRoadmap = lazy(() => import('./pages/gtm/ExpansionRoadmap'));
const PilotClosingNarrative = lazy(() => import('./pages/gtm/PilotClosingNarrative'));
const EnterpriseDeckScreenshots = lazy(() => import('./pages/gtm/EnterpriseDeckScreenshots'));
const SandboxSessionScript = lazy(() => import('./pages/gtm/SandboxSessionScript'));
const PilotDashboard = lazy(() => import('./pages/gtm/PilotDashboard'));
const OpsDashboard = lazy(() => import('./pages/gtm/OpsDashboard'));
// P1: Observability Command Center — Production Hardening
const ObservabilityDashboard = lazy(() => import('./pages/ObservabilityCommandCenter'));
// P3: Live Rail Replay Console — Transparent Black Box
const ReplayConsole = lazy(() => import('./pages/ReplayConsole'));
// Workspace — authenticated partner surfaces
const PilotControlRoom = lazy(() => import('./pages/workspace/PilotControlRoom'));
const OpsWorkspace = lazy(() => import('./pages/workspace/OpsWorkspace'));
const SandboxActivation = lazy(() => import('./pages/workspace/SandboxActivation'));
// Diligence Review — public secure reviewer surface
const DiligenceReviewPage = lazy(() => import('./pages/DiligenceReviewPage'));
// ═══ GRANULLO — Product Primitive Pages ═══
const V3BoundariesPage = lazy(() => import('./pages/V3BoundariesPage'));
const V3TriggersPage = lazy(() => import('./pages/V3TriggersPage'));
const V3TrustedNetworkPage = lazy(() => import('./pages/V3TrustedNetworkPage'));
const V3TwinMemoryPage = lazy(() => import('./pages/V3TwinMemoryPage'));
const V3CompliancePassportPage = lazy(() => import('./pages/V3CompliancePassportPage'));
const V3RailReadinessPage = lazy(() => import('./pages/V3RailReadinessPage'));
const V3LabsPage = lazy(() => import('./pages/V3LabsPage'));
const V3ProgramBuilder = lazy(() => import('./pages/V3ProgramBuilder'));
// Granullo CRM — Commercial Intelligence Dashboard
const CRMDashboard = lazy(() => import('./pages/CRMDashboard'));
// Granullo Momentum — Follow-Up Orchestration + Studio
const MomentumDashboard = lazy(() => import('./pages/MomentumDashboard'));
const WorldReadyDashboard = lazy(() => import('./pages/WorldReadyDashboard'));
// Granullo Intelligence — Public Conviction Page
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'));
const IntelligenceDiscoverChapter = lazy(() => import('./pages/intelligence/IntelligenceDiscoverChapter'));
const IntelligenceMemoryChapter = lazy(() => import('./pages/intelligence/IntelligenceMemoryChapter'));
const IntelligenceMomentumChapter = lazy(() => import('./pages/intelligence/IntelligenceMomentumChapter'));
const IntelligenceWorldModelsChapter = lazy(() => import('./pages/intelligence/IntelligenceWorldModelsChapter'));
const IntelligenceHowItThinksChapter = lazy(() => import('./pages/intelligence/IntelligenceHowItThinksChapter'));
const CapitalWithCoordinatesPage = lazy(() => import('./pages/CapitalWithCoordinatesPage'));
// Investor Intelligence Room
const InvestorIntelligenceRoom = lazy(() => import('./pages/investor/InvestorIntelligenceRoom'));
const IPLedgerPage = lazy(() => import('./pages/investor/IPLedger'));
const MoatVisualizationPage = lazy(() => import('./pages/investor/MoatVisualization'));
const FinancialTheoremPage = lazy(() => import('./pages/investor/FinancialTheorem'));
const ReturnSimulatorPage = lazy(() => import('./pages/investor/ReturnSimulator'));
const InvestorSandboxNewPage = lazy(() => import('./pages/investor/InvestorSandboxNew'));
const InvestorProgressPage = lazy(() => import('./pages/investor/InvestorProgress'));
const DiligenceExportPage = lazy(() => import('./pages/investor/DiligenceExport'));
// Conviction System V2
const TruthTrackingPage = lazy(() => import('./pages/investor/TruthTracking'));
const ValuationLabPage = lazy(() => import('./pages/investor/ValuationLab'));
const ConvictionReplayPage = lazy(() => import('./pages/investor/ConvictionReplay'));
const BoardObserverPage = lazy(() => import('./pages/investor/BoardObserver'));
// Trust Room
const TrustRoomOverviewPage = lazy(() => import('./pages/trust-room/TrustRoomOverview'));
const TrustRoomTruthTrackingPage = lazy(() => import('./pages/trust-room/TrustRoomTruthTracking'));
const TrustRoomValuationLabPage = lazy(() => import('./pages/trust-room/TrustRoomValuationLab'));
const TrustRoomConvictionReplayPage = lazy(() => import('./pages/trust-room/TrustRoomConvictionReplay'));
const TrustRoomBoardObserverPage = lazy(() => import('./pages/trust-room/TrustRoomBoardObserver'));

// Investor Auth Gate
import { InvestorAuthProvider, InvestorLoginGate } from './pages/investor/InvestorAuth';
import { StakeholderConvictionProvider } from './contexts/StakeholderConvictionStateSpine';
import TrustRoomGate from './pages/trust-room/TrustRoomGate';
import TrustRoomSurface from './pages/trust-room/TrustRoomSurface';
function InvestorGate({ children }) {
  return <InvestorAuthProvider><InvestorLoginGate><StakeholderConvictionProvider>{children}</StakeholderConvictionProvider></InvestorLoginGate></InvestorAuthProvider>;
}
// Capital Routing Command Center V2
const CapitalRoutingCommandCenter = lazy(() => import('./pages/CapitalRoutingCommandCenter'));
// P6: Role Lens first-visit check
// hasSeenStart removed — start splash disabled
// P6 V2: Route-specific document titles
import { useDocumentTitle } from './hooks/useDocumentTitle';
// SYSTEM INVARIANT: Automatic Lens Zone Observer for dynamic platform adaptation
// NOTE: lensZoneObserver removed — orphan import (never used)

import { useObservation } from './hooks/useObservation';

const AuthCallback = lazy(() => import('./pages/AuthCallback').then(m => ({ default: m.AuthCallback })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register'));
const RecoverRequest = lazy(() => import('./pages/RecoverRequest').then(m => ({ default: m.RecoverRequest })));
const RecoverReset = lazy(() => import('./pages/RecoverReset').then(m => ({ default: m.RecoverReset })));
const TwoFASetup = lazy(() => import('./pages/TwoFASetup').then(m => ({ default: m.TwoFASetup })));
const AdminSetup = lazy(() => import('./pages/AdminSetup'));
const DemoLanding = lazy(() => import('./pages/DemoLanding').then(m => ({ default: m.DemoLanding })));
const DemoPage = lazy(() => import('./pages/DemoPage').then(m => ({ default: m.DemoPage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Organizations = lazy(() => import('./pages/Organizations').then(m => ({ default: m.Organizations })));
const Programs = lazy(() => import('./pages/Programs').then(m => ({ default: m.Programs })));
const ProgramDetail = lazy(() => import('./pages/ProgramDetail').then(m => ({ default: m.ProgramDetail })));
const Policies = lazy(() => import('./pages/Policies').then(m => ({ default: m.Policies })));
const PolicyDetail = lazy(() => import('./pages/PolicyDetail').then(m => ({ default: m.PolicyDetail })));
const PolicySimulator = lazy(() => import('./pages/PolicySimulator').then(m => ({ default: m.PolicySimulator })));
const Beneficiaries = lazy(() => import('./pages/Beneficiaries').then(m => ({ default: m.Beneficiaries })));
const BeneficiaryDetail = lazy(() => import('./pages/BeneficiaryDetail').then(m => ({ default: m.BeneficiaryDetail })));
const Merchants = lazy(() => import('./pages/Merchants').then(m => ({ default: m.Merchants })));
const MerchantDetail = lazy(() => import('./pages/MerchantDetail').then(m => ({ default: m.MerchantDetail })));
const Transactions = lazy(() => import('./pages/Transactions').then(m => ({ default: m.Transactions })));
const TransactionDetail = lazy(() => import('./pages/TransactionDetail').then(m => ({ default: m.TransactionDetail })));
const Approvals = lazy(() => import('./pages/Approvals').then(m => ({ default: m.Approvals })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const AuditLogs = lazy(() => import('./pages/AuditLogs').then(m => ({ default: m.AuditLogs })));
const ComplianceDashboard = lazy(() => import('./pages/ComplianceDashboard').then(m => ({ default: m.ComplianceDashboard })));
const ComplianceAlerts = lazy(() => import('./pages/ComplianceAlerts').then(m => ({ default: m.ComplianceAlerts })));
const EscrowConsole = lazy(() => import('./pages/EscrowConsole').then(m => ({ default: m.EscrowConsole })));
const Support = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));
const Guides = lazy(() => import('./pages/Guides').then(m => ({ default: m.Guides })));
// Enterprise Deal Infrastructure
const DealsPage = lazy(() => import('./pages/DealsPage'));
const DealDetailPage = lazy(() => import('./pages/DealDetailPage'));
const CreateDealPage = lazy(() => import('./pages/CreateDealPage'));
const CreateCommitmentPage = lazy(() => import('./pages/CreateCommitmentPage'));
const CommitmentDetailPage = lazy(() => import('./pages/CommitmentDetailPage'));
// PHASE 2: Deal Rooms
const DealRoom = lazy(() => import('./components/rooms/DealRoom'));
// Design System Check (dev only)
const DesignSystemCheck = lazy(() => import('./pages/DesignSystemCheck'));
// Token Provenance Debug Overlay
import TokenProvenanceOverlay from './components/debug/TokenProvenanceOverlay';
const GuideDetail = lazy(() => import('./pages/GuideDetail').then(m => ({ default: m.GuideDetail })));
const PlaybookDetail = lazy(() => import('./pages/PlaybookDetail').then(m => ({ default: m.PlaybookDetail })));
const UserManagement = lazy(() => import('./pages/UserManagement').then(m => ({ default: m.UserManagement })));
const AdvancedProgrammability = lazy(() => import('./pages/AdvancedProgrammability').then(m => ({ default: m.AdvancedProgrammability })));
const RequestAccess = lazy(() => import('./pages/RequestAccess').then(m => ({ default: m.RequestAccess })));
const AccessRequests = lazy(() => import('./pages/AccessRequests').then(m => ({ default: m.AccessRequests })));
const TemplateManagement = lazy(() => import('./pages/TemplateManagement').then(m => ({ default: m.TemplateManagement })));
const ExpirationTracking = lazy(() => import('./pages/ExpirationTracking').then(m => ({ default: m.ExpirationTracking })));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings').then(m => ({ default: m.SecuritySettings })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const OrganizationProfile = lazy(() => import('./pages/OrganizationProfile').then(m => ({ default: m.OrganizationProfile })));
import HomePage from './pages/HomePage';
const ProofLoopPage = lazy(() => import('./pages/ProofLoopPage'));
// PHASE G1: Autonomous Conviction Gateway
const SimulationRoomPage = lazy(() => import('./pages/SimulationRoomPage'));
// PLAY OS: Public pre-login role-based play walkthroughs
// Play — Decision Chain Game
const PlayLanding = lazy(() => import('./pages/play/PlayLanding'));
const PlayLeaderboard = lazy(() => import('./pages/play/PlayLeaderboard'));
const RailPartnerSimulation = lazy(() => import('./pages/simulations/RailPartnerSimulation'));
const SponsorSimulation = lazy(() => import('./pages/simulations/SponsorSimulation'));
const InvestorSimulation = lazy(() => import('./pages/simulations/InvestorSimulation'));
const RegulatorSimulation = lazy(() => import('./pages/simulations/RegulatorSimulation'));
const AgenticCommerceSimulation = lazy(() => import('./pages/simulations/AgenticCommerceSimulation'));
const CBDCBenefitsSimulation = lazy(() => import('./pages/simulations/CBDCBenefitsSimulation'));
// Partner Sandbox OS — Technical Diligence Workspace
const PartnerSandboxWorkspace = lazy(() => import('./pages/PartnerSandboxWorkspace'));
const PartnerSandboxAdmin = lazy(() => import('./pages/PartnerSandboxAdmin').then(m => ({ default: m.PartnerSandboxAdmin })));
// PHASE G4.5: Demand Pulse UI
const DemandPulsePage = lazy(() => import('./pages/DemandPulsePage'));
// FOUNDER DIRECTIVE: Full Registry Explorer Page
const RegistryPage = lazy(() => import('./pages/RegistryPage'));
// GRANULLO PHASE 2: Authority Matrix
const AuthorityMatrixPage = lazy(() => import('./pages/AuthorityMatrixPage'));
const DeveloperSettings = lazy(() => import('./pages/DeveloperSettings').then(m => ({ default: m.DeveloperSettings })));
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal').then(m => ({ default: m.DeveloperPortal })));
const IntegrationAppDetails = lazy(() => import('./pages/IntegrationAppDetails').then(m => ({ default: m.IntegrationAppDetails })));
const BankRequests = lazy(() => import('./pages/BankRequests'));
const BankRequestDetail = lazy(() => import('./pages/BankRequestDetail'));
const NewBankRequest = lazy(() => import('./pages/NewBankRequest'));
// OpsDashboard removed - using OpsPage (A4) instead
const HowGranulloWorks = lazy(() => import('./pages/HowGranulloWorks').then(m => ({ default: m.HowGranulloWorks })));
const WalkthroughDetail = lazy(() => import('./pages/HowGranulloWorks').then(m => ({ default: m.WalkthroughDetail })));
const ProgramWalkthroughs = lazy(() => import('./pages/ProgramWalkthroughs').then(m => ({ default: m.ProgramWalkthroughs })));
const ProgramWalkthroughDetail = lazy(() => import('./pages/ProgramWalkthroughs').then(m => ({ default: m.ProgramWalkthroughDetail })));
const GranulloIn5Sentences = lazy(() => import('./pages/GranulloIn5Sentences'));
const DeployPage = lazy(() => import('./pages/DeployPage'));
const LearningHub = lazy(() => import('./pages/LearningHub'));
const AskGranullo = lazy(() => import('./pages/AskGranullo'));
const TrustLayer = lazy(() => import('./pages/TrustLayer'));
const WhatIsTrustLayer = lazy(() => import('./pages/TrustAssurancePages').then(m => ({ default: m.WhatIsTrustLayer })));
const WhatWeProve = lazy(() => import('./pages/TrustAssurancePages').then(m => ({ default: m.WhatWeProve })));
const WhyItMatters = lazy(() => import('./pages/TrustAssurancePages').then(m => ({ default: m.WhyItMatters })));
const WhatItIsNot = lazy(() => import('./pages/TrustAssurancePages').then(m => ({ default: m.WhatItIsNot })));
const TrustLayerExamples = lazy(() => import('./pages/TrustLayerExamples').then(m => ({ default: m.TrustLayerExamples })));
const IdentityAssuranceExample = lazy(() => import('./pages/TrustLayerExamples').then(m => ({ default: m.IdentityAssuranceExample })));
const PolicyComplianceExample = lazy(() => import('./pages/TrustLayerExamples').then(m => ({ default: m.PolicyComplianceExample })));
const HoverGallery = lazy(() => import('./pages/HoverGallery').then(m => ({ default: m.HoverGallery })));
const ReviewClarifications = lazy(() => import('./pages/ReviewClarifications').then(m => ({ default: m.ReviewClarifications })));
const ReviewThreadDetail = lazy(() => import('./pages/ReviewThreadDetail').then(m => ({ default: m.ReviewThreadDetail })));
const CampaignBrandVault = lazy(() => import('./pages/CampaignBrandVault').then(m => ({ default: m.CampaignBrandVault })));
const TrustPilot = lazy(() => import('./pages/TrustPilot').then(m => ({ default: m.TrustPilot })));
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard').then(m => ({ default: m.ExecutiveDashboard })));
const SettingsIntegrations = lazy(() => import('./pages/SettingsIntegrations').then(m => ({ default: m.SettingsIntegrations })));
const WebhookRetries = lazy(() => import('./pages/WebhookRetries').then(m => ({ default: m.WebhookRetries })));
const CSRPartners = lazy(() => import('./pages/CSRPartners').then(m => ({ default: m.CSRPartners })));
// P2.0 Intent Records UI
const IntentListPage = lazy(() => import('./pages/IntentListPage'));
const IntentDetailPage = lazy(() => import('./pages/IntentDetailPage'));
// P1: Intent Records — Adoption Surfaces
const IntentRecordsPage = lazy(() => import('./pages/IntentRecordsPage'));
const CSRAllocations = lazy(() => import('./pages/CSRAllocations').then(m => ({ default: m.CSRAllocations })));
const CSRPublicApply = lazy(() => import('./pages/CSRPublicApply').then(m => ({ default: m.CSRPublicApply })));
const CSRPrograms = lazy(() => import('./pages/CSRPrograms').then(m => ({ default: m.CSRPrograms })));
const CSRCommunication = lazy(() => import('./pages/CSRCommunication').then(m => ({ default: m.CSRCommunication })));
const CSRMerchantGovernance = lazy(() => import('./pages/CSRMerchantGovernance').then(m => ({ default: m.CSRMerchantGovernance })));
const OrgSettings = lazy(() => import('./pages/OrgSettings').then(m => ({ default: m.OrgSettings })));
const CSRAnalytics = lazy(() => import('./pages/CSRAnalytics').then(m => ({ default: m.CSRAnalytics })));
const CSRInviteAccept = lazy(() => import('./pages/CSRInviteAccept').then(m => ({ default: m.CSRInviteAccept })));
const CSRThreadedCommunication = lazy(() => import('./pages/CSRThreadedCommunication').then(m => ({ default: m.CSRThreadedCommunication })));
const CSRThreadDetail = lazy(() => import('./pages/CSRThreadedCommunication').then(m => ({ default: m.CSRThreadDetail })));
const CSRGovernanceMap = lazy(() => import('./pages/CSRGovernanceMap').then(m => ({ default: m.CSRGovernanceMap })));
const ImpactIntelligence = lazy(() => import('./pages/ImpactIntelligence').then(m => ({ default: m.ImpactIntelligence })));
const CSRHub = lazy(() => import('./pages/CSRHub').then(m => ({ default: m.CSRHub })));
const CSRExecution = lazy(() => import('./pages/CSRExecution').then(m => ({ default: m.CSRExecution })));
const CSRInsights = lazy(() => import('./pages/CSRInsights').then(m => ({ default: m.CSRInsights })));
const PilotReadinessPage = lazy(() => import('./pages/PilotReadinessPage'));
const NeedsDiscoveryPage = lazy(() => import('./pages/NeedsDiscoveryPage'));
const FoundationNetworkPage = lazy(() => import('./pages/FoundationNetworkPage'));
const DiscoverTestDashboard = lazy(() => import('./pages/DiscoverTestDashboard'));
const PilotPackDetailPage = lazy(() => import('./pages/PilotPackDetailPage'));
const MerchantNetworkPage = lazy(() => import('./pages/MerchantNetworkPage'));
const MerchantDetailPage = lazy(() => import('./pages/MerchantDetailPage'));
const UsingGranullo = lazy(() => import('./pages/UsingGranullo').then(m => ({ default: m.UsingGranullo })));
const Glossary = lazy(() => import('./pages/Glossary').then(m => ({ default: m.Glossary })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const RegulatoryWalkthrough = lazy(() => import('./pages/RegulatoryWalkthrough').then(m => ({ default: m.RegulatoryWalkthrough })));
const BankStartHere = lazy(() => import('./pages/BankStartHere'));
const AuditNarrative = lazy(() => import('./pages/AuditNarrative').then(m => ({ default: m.AuditNarrative })));
const InsuranceClaimsScenario = lazy(() => import('./pages/scenarios/InsuranceClaimsScenario'));
const EmployeeBenefitsScenario = lazy(() => import('./pages/scenarios/EmployeeBenefitsScenario'));
const CSRGrantsScenario = lazy(() => import('./pages/scenarios/CSRGrantsScenario'));
const BankCardApplications = lazy(() => import('./pages/BankCardApplications').then(m => ({ default: m.BankCardApplications })));
const BankOnboarding = lazy(() => import('./pages/BankOnboarding').then(m => ({ default: m.BankOnboarding })));
// PHASE 5: Live World Entry — Narrative + Trust
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const ReadinessCheck = lazy(() => import('./pages/ReadinessCheck'));
// A4: Production Ops Dashboard
const OpsPage = lazy(() => import('./pages/OpsPage'));
const VerifierLinksAdmin = lazy(() => import('./pages/VerifierLinksAdmin'));
const BankExecutionLedger = lazy(() => import('./pages/BankExecutionLedger'));
const ExecutionRoom = lazy(() => import('./pages/ExecutionRoom'));
const GovernanceHistory = lazy(() => import('./pages/GovernanceHistory'));
const GovernanceIntelligence = lazy(() => import('./pages/GovernanceIntelligence'));
const DPPSimulator = lazy(() => import('./pages/DPPSimulator'));
const ProgramReadiness = lazy(() => import('./pages/ProgramReadiness'));
const CommunicationDoctrine = lazy(() => import('./pages/CommunicationDoctrine'));
const RoleOnboarding = lazy(() => import('./pages/RoleOnboarding'));  
const WhyGranullo = lazy(() => import('./pages/WhyGranullo'));
const AuditorView = lazy(() => import('./pages/AuditorView'));
const ProgramConversations = lazy(() => import('./pages/ProgramConversations'));
const GranulloVisualFlow = lazy(() => import('./pages/GranulloVisualFlow'));
const HowGranulloWorksCanonical = lazy(() => import('./pages/HowGranulloWorksCanonical'));
const BankIntegrationGuide = lazy(() => import('./pages/BankIntegrationGuide'));
const RegulatoryReadiness = lazy(() => import('./pages/RegulatoryReadiness'));
const RegulatorStartHere = lazy(() => import('./pages/RegulatorStartHere'));
const BankHome = lazy(() => import('./pages/BankHome'));
const MerchantControlCenter = lazy(() => import('./pages/MerchantControlCenter'));
const BankIntegrations = lazy(() => import('./pages/BankIntegrations'));
const BankAgreements = lazy(() => import('./pages/BankAgreements'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ClientHome = lazy(() => import('./pages/ClientHome'));
const ClientStartHere = lazy(() => import('./pages/ClientStartHere'));
const ClientPrograms = lazy(() => import('./pages/ClientPrograms'));
const ClientReports = lazy(() => import('./pages/ClientReports'));
const ClientReportDashboard = lazy(() => import('./pages/ClientReportDashboard'));
const DevSettings = lazy(() => import('./pages/DevSettings'));
const DesignSystemQA = lazy(() => import('./pages/DesignSystemQA'));
const InternalDocs = lazy(() => import('./pages/InternalDocs'));
const Labs = lazy(() => import('./pages/Labs'));
// Phase 8: Labs Interactive Layers
const ScenarioStudio = lazy(() => import('./pages/labs').then(m => ({ default: m.ScenarioStudio })));
const InteractiveMaps = lazy(() => import('./pages/labs').then(m => ({ default: m.InteractiveMaps })));
const GovernanceDashboard = lazy(() => import('./pages/labs').then(m => ({ default: m.GovernanceDashboard })));
const NavigationAudit = lazy(() => import('./pages/NavigationAudit'));
const Trust = lazy(() => import('./pages/Trust'));
const EpistemicPolicy = lazy(() => import('./pages/EpistemicPolicy'));
const Invariants = lazy(() => import('./pages/Invariants'));
const PostSignupActivation = lazy(() => import('./pages/PostSignupActivation'));
const ExternalSourceDashboard = lazy(() => import('./pages/ExternalSourceDashboard'));
const GovernanceMirror = lazy(() => import('./pages/GovernanceMirror'));
const RegulatorCopilot = lazy(() => import('./pages/RegulatorCopilot'));
const MerchantConsole = lazy(() => import('./pages/MerchantConsole'));
const SpendVisibility = lazy(() => import('./pages/SpendVisibility'));
const Receipts = lazy(() => import('./pages/Receipts'));
const CFOVault = lazy(() => import('./pages/CFOVault'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const PlatformPage = lazy(() => import('./pages/PlatformPage'));
const PlatformGranulloPassPage = lazy(() => import('./pages/PlatformGranulloPassPage'));
const PlatformPaymentTwinsPage = lazy(() => import('./pages/PlatformPaymentTwinsPage'));
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'));
const ProofPage = lazy(() => import('./pages/ProofPage'));
const LaunchObservabilityPage = lazy(() => import('./pages/LaunchObservabilityPage'));
const SimulatePage = lazy(() => import('./pages/SimulatePage'));
// Phase 6: Commit Capacity Planner
const CommitCapacityPlanner = lazy(() => import('./pages/CommitCapacityPlanner'));
// Phase 1B: Commit Wallet & Billing
const BillingCommits = lazy(() => import('./pages/BillingCommits'));
// Phase 7: Account Center with Commit Wallet
const AccountCenterPage = lazy(() => import('./pages/AccountCenterPage'));
// Phase 8: Commit Grant Pool
const CommitGrantPoolPage = lazy(() => import('./pages/CommitGrantPoolPage'));
// Phase 9: Investor Sandbox
// Phase 8.5-D: Zero-Knowledge Governance Vault
const VaultDashboard = lazy(() => import('./pages/VaultDashboard'));
// Phase A: Onboarding Structural Engine
const OnboardingHub = lazy(() => import('./pages/OnboardingHub'));
// P4.4: Preparedness Delta
const PreparednessPage = lazy(() => import('./pages/PreparednessPage'));
// P5: CBDC Infrastructure Page
const CBDCPage = lazy(() => import('./pages/CBDCPage'));
// P5: Start Here Narrative Page
const StartHerePage = lazy(() => import('./pages/StartHerePage'));
// P6: First-Run Role Selection Interstitial
const StartPage = lazy(() => import('./pages/StartPage'));
// P6.2: Review Mode UI
import { ReviewModeBanner } from './components/auth/ReviewModeBadge';
import { ClarityV2Provider } from './contexts/ClarityV2Context';
// P6: Quantum Safe Vault
const QSVPage = lazy(() => import('./vault_qsv').then(m => ({ default: m.QSVPage })));
import { ClarityPrompt } from './components/clarity/ClarityPrompt';
import { ClarityOnIndicator } from './components/clarity/ClarityAmbientLayer';
import { ClarityScrollNarrator } from './components/clarity/ClarityScrollNarrator';
// GRANULLO_COMMITMENT_INFRASTRUCTURE_v1.1: Explain Toggle (Clarity Successor)
import { ExplainProvider, ExplainStyles } from './components/explain/ExplainToggle';
// P5: Founder Console & Global Navigation
import { FounderProvider, useFounder } from './contexts/FounderContext';
import { FounderNav } from './components/founder/FounderNav';
const FounderConsolePage = lazy(() => import('./pages/FounderConsolePage'));
const FounderIntelligencePage = lazy(() => import('./pages/FounderIntelligencePage'));
const FounderGTMPage = lazy(() => import('./pages/FounderGTMPage'));
const FounderFrictionPage = lazy(() => import('./pages/FounderFrictionPage'));
const FounderInvestorPage = lazy(() => import('./pages/FounderInvestorPage'));
const FounderMarketingPage = lazy(() => import('./pages/FounderMarketingPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// PHASE 5: Network Intake Infrastructure - Founder Review Console
const NetworkReviewPage = lazy(() => import('./pages/NetworkReviewPage'));

// P6.2: Investor Narrative Tooling
const InvestorsPage = lazy(() => import('./pages/InvestorsPage'));

// Lens System
const LensPage = lazy(() => import('./pages/LensPage'));

// Commit Grants Pool
const PoolStatus = lazy(() => import('./pages/PoolStatus'));
const GrantApply = lazy(() => import('./pages/GrantApply'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const PoolContribute = lazy(() => import('./pages/PoolContribute'));

// Phase D: Structural Telemetry Panel
const TelemetryDashboard = lazy(() => import('./pages/TelemetryDashboard'));

// Phase E: Ongoing Audit Module
const AuditTimeline = lazy(() => import('./pages/AuditTimeline'));
const AuditCompliance = lazy(() => import('./pages/AuditCompliance'));

// Phase Z: Investor Simulation Mode
const SimulationPage = lazy(() => import('./pages/SimulationPage'));

// War Room Mode
const WarRoomPage = lazy(() => import('./pages/WarRoomPage'));

// Insurance Layer
const InsurersPage = lazy(() => import('./pages/InsurersPage'));
const DisputePackages = lazy(() => import('./pages/DisputePackages'));

// B2: LP / External Verifier Experience
const VerifierDeals = lazy(() => import('./pages/VerifierDeals'));
const VerifierDealRoom = lazy(() => import('./pages/VerifierDealRoom'));

// C1: Hiring & People Governance (POST-HIRE ONLY)
const HiringGovernance = lazy(() => import('./pages/HiringGovernance'));

// B1 Extension: Org-Level Governance Signals
const OrgGovernanceSignals = lazy(() => import('./pages/OrgGovernanceSignals'));

// P1-B: Audit Ledger — Read-Only Truth Surface
const LedgerPage = lazy(() => import('./pages/LedgerPage'));
const VerifierLedgerPage = lazy(() => import('./pages/VerifierLedgerPage'));

// Verifier Link Landing
const VerifierLinkLanding = lazy(() => import('./pages/VerifierLinkLanding'));

// Phase 2: Sandbox + Verifier + Founder Control Panel
const SandboxLanding = lazy(() => import('./pages/SandboxLanding'));
const SandboxScenario = lazy(() => import('./pages/SandboxScenario'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const FounderLinksPage = lazy(() => import('./pages/FounderLinksPage'));
const ProductionControlPage = lazy(() => import('./pages/ProductionControlPage'));
const RevenueBoardroomPage = lazy(() => import('./pages/RevenueBoardroomPage'));
const ValuationBoardroomPage = lazy(() => import('./pages/ValuationBoardroomPage'));

// Public Trust Page
const WhatGranulloIs = lazy(() => import('./pages/WhatGranulloIs'));

// Granullo 2.0 — New Pages
const LiveProgramsPage = lazy(() => import('./pages/LiveProgramsPage'));
const TryGranulloSandboxStandalone = lazy(() => import('./components/homepage/TryGranulloSandbox'));

// Architecture Page
const ArchitecturePage = lazy(() => import('./pages/ArchitecturePage'));
// Institutional Entry Pages
const PolicyBriefPage = lazy(() => import('./pages/PolicyBriefPage'));
const OnePagePage = lazy(() => import('./pages/OnePagePage'));
const RequestDemoPage = lazy(() => import('./pages/RequestDemoPage'));

// BANK OPERATIONAL WORKFLOW LAYER
const BankSchemaBuilder = lazy(() => import('./pages/BankSchemaBuilder'));
const SponsorApplyBanks = lazy(() => import('./pages/SponsorApplyBanks'));
const BankReviewDashboard = lazy(() => import('./pages/BankReviewDashboard'));
const PackViewer = lazy(() => import('./pages/PackViewer'));

// PHASE 4: GLASS + ATLAS VIEW
const AtlasPage = lazy(() => import('./pages/AtlasPage'));
const GlassPage = lazy(() => import('./pages/GlassPage'));
const GranulloAtlasPage = lazy(() => import('./pages/GranulloAtlasPage'));
const DecisionForkPage = lazy(() => import('./pages/DecisionForkPage'));
const AtlasExposurePage = lazy(() => import('./pages/AtlasExposurePage'));
const GlassInspectorPage = lazy(() => import('./pages/GlassInspectorPage'));

// FOUNDER DIRECTIVE: Global Commitment Infrastructure Layer
const GCIPublicLookup = lazy(() => import('./pages/GCIPublicLookup'));

// PHASE 7: GRANULLO INTELLIGENCE SURFACE
const IntelligenceLayout = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceLayout })));
const IntelligenceOverview = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceOverview })));
const IntelligenceCIRS = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceCIRS })));
const IntelligenceDSE = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceDSE })));
const IntelligenceSFCI = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceSFCI })));
const IntelligenceIIS = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceIIS })));
const IntelligenceEML = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceEML })));
const IntelligencePassport = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligencePassport })));
const IntelligenceCID = lazy(() => import('./pages/intelligence').then(m => ({ default: m.IntelligenceCID })));

// PHASE D: Global Exposure Map
const ExposureMapPage = lazy(() => import('./pages/ExposureMapPage'));

// Phase 11.4 — Program Lifecycle Governance UI
const GovernanceProgramsPage = lazy(() => import('./pages/GovernanceProgramsPage'));
const GovernanceProgramDetailPage = lazy(() => import('./pages/GovernanceProgramDetailPage'));

// PHASE 7.1: CONSUMER SURFACE
const ConsumerLayout = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerLayout })));
const ConsumerOverview = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerOverview })));
const ConsumerMap = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerMap })));
const ConsumerTimeline = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerTimeline })));
const ConsumerPassport = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerPassport })));
const ConsumerAbout = lazy(() => import('./pages/consumer').then(m => ({ default: m.ConsumerAbout })));

// PHASE 2: IMPACT INTELLIGENCE LAYER — Control Room UI
const ControlRoomPage = lazy(() => import('./pages/control-room'));

// PHASE 3: NETWORK ENTRY LAYER
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const SandboxPage = lazy(() => import('./pages/SandboxPage'));
const ProgramCreatorsJoinPage = lazy(() => import('./pages/join').then(m => ({ default: m.ProgramCreatorsJoinPage })));
const BanksJoinPage = lazy(() => import('./pages/join').then(m => ({ default: m.BanksJoinPage })));
const MerchantsJoinPage = lazy(() => import('./pages/join').then(m => ({ default: m.MerchantsJoinPage })));

// PHASE 6.11: Granullo Security Toolkit
const SecurityToolkitPage = lazy(() => import('./pages/SecurityToolkitPage'));

// PHASE 4: DEMAND MARKETPLACE EXPERIENCE (UI)
const MarketplacePage = lazy(() => import('./pages/marketplace'));

// PHASE F: UX ARCHITECTURE — New Surfaces
const ObservatoryPage = lazy(() => import('./pages/ObservatoryPage'));
const SimulationEnvironmentPage = lazy(() => import('./pages/SimulationEnvironmentPage'));
const IntegrateGranulloPage = lazy(() => import('./pages/IntegrateGranulloPage'));
const IntegrationsHubPage = lazy(() => import('./pages/integrations/IntegrationsHubPage'));
const IntegrationDetailPage = lazy(() => import('./pages/integrations/IntegrationDetailPage'));
const SupplantSandboxPage = lazy(() => import('./pages/supplant/SupplantSandboxPage'));
const SupplantEmbeddedPage = lazy(() => import('./pages/supplant/SupplantEmbeddedPage'));
const SupPlantCanonicalEntry = lazy(() => import('./pages/supplant/SupPlantCanonicalEntry'));
const SupplantProgramConsole = lazy(() => import('./pages/supplant/SupplantProgramConsole'));
const FarmerAppPage = lazy(() => import('./pages/supplant/FarmerAppPage'));
const MyEntitlement = lazy(() => import('./pages/supplant/MyEntitlement'));
const FactoryConsole = lazy(() => import('./pages/supplant/FactoryConsole'));
const VirtualAssetLab = lazy(() => import('./pages/supplant/VirtualAssetLab'));
const GranulloCoreInspector = lazy(() => import('./pages/granullo/GranulloCoreInspector'));
const GranulloExecutionConsole = lazy(() => import('./pages/granullo/GranulloExecutionConsole'));
const NetworkSimulationPage = lazy(() => import('./pages/NetworkSimulationPage'));
const MissionControlPage = lazy(() => import('./pages/MissionControlPage'));
const InvestorDemoPage = lazy(() => import('./pages/InvestorDemoPage'));

// P1: v2 GTM Demo Flows + Meeting-Safe Conviction Flows
const DemoFlowPage = lazy(() => import('./pages/DemoFlowPage'));

// FOUNDER DIRECTIVE: Organization Commitment Dashboard
const OrganizationCommitmentDashboard = lazy(() => import('./pages/OrganizationCommitmentDashboard'));

// GRANULLO PLATFORM EVOLUTION: Phase 2 — Program Structure Dashboard
const ProgramStructurePage = lazy(() => import('./pages/ProgramStructurePage'));

// Consumer surface feature flag
const CONSUMER_SURFACE_ENABLED = process.env.REACT_APP_CONSUMER_ENABLED === 'true' || true; // Default enabled for demo

// External reviewer roles (for review mode banner)
const EXTERNAL_REVIEW_ROLES = ['regulator_review', 'bank_exec', 'bank_tech_review', 'investor_review'];

/**
 * ScopedNotFound — an unknown URL must not expose operator chrome.
 *
 * The sandbox evaluator has exactly one surface. Dropping them on the shared
 * 404, which renders inside the Granullo operator shell, shows them a product
 * they are not evaluating. Send them back to their own surface instead.
 */
function ScopedNotFound() {
  const { user } = useAuth();
  if (user?.role === 'supplant_sandbox') {
    return <Navigate to="/sandbox/supplant" replace />;
  }
  if (user?.role === 'policy_lab_sandbox') {
    return <Navigate to="/sandbox/policy-lab" replace />;
  }
  return <NotFoundPage />;
}

/**
 * ReviewModeWrapper — Shows review mode banner for external reviewers
 */
function ReviewModeWrapper() {
  const { user } = useAuth();
  
  if (!user || !EXTERNAL_REVIEW_ROLES.includes(user.role)) {
    return null;
  }
  
  return <ReviewModeBanner user={user} />;
}

/**
 * FounderModeOffset — Adds top padding when FounderNav is visible
 */
function FounderModeOffset({ children }) {
  const { isFounder } = useFounder();
  const location = useLocation();
  const isMoment = location.pathname.startsWith('/moment/') || location.pathname.startsWith('/partner') || location.pathname.startsWith('/workspace');
  return (
    <div className={isFounder && !isMoment ? 'pt-10' : ''}>
      {children}
    </div>
  );
}

/**
 * FirstVisitRedirect — DISABLED
 * Previously redirected first-time visitors to /start (persona selection splash).
 * Now disabled per product directive: land directly on main homepage.
 * The /start route remains accessible via direct navigation for legacy links.
 */
function FirstVisitRedirect() {
  return null;
}

// Possibilities Pages
const Possibilities = lazy(() => import('./pages/Possibilities'));
const GovernmentTendersScenario = lazy(() => import('./pages/possibilities/GovernmentTendersScenario'));
const ESGLinkedBudgetsScenario = lazy(() => import('./pages/possibilities/ESGLinkedBudgetsScenario'));
const ClimateFinanceScenario = lazy(() => import('./pages/possibilities/ClimateFinanceScenario'));
const SolarFarmScenario = lazy(() => import('./pages/possibilities/SolarFarmScenario'));
const CarbonCreditsScenario = lazy(() => import('./pages/possibilities/CarbonCreditsScenario'));
const PublicPrivateFundsScenario = lazy(() => import('./pages/possibilities/PublicPrivateFundsScenario'));
const ScholarshipScenario = lazy(() => import('./pages/possibilities/ScholarshipScenario'));
const UniversalIncomeScenario = lazy(() => import('./pages/possibilities/UniversalIncomeScenario'));
const UBIGovernanceHub = lazy(() => import('./pages/UBIGovernanceHub'));
// Corporate Program Scenarios
const LearningAndDevelopmentScenario = lazy(() => import('./pages/possibilities/LearningAndDevelopmentScenario'));
const MentalHealthAllowanceScenario = lazy(() => import('./pages/possibilities/MentalHealthAllowanceScenario'));
const RemoteWorkStipendScenario = lazy(() => import('./pages/possibilities/RemoteWorkStipendScenario'));
const DEIInclusionGrantScenario = lazy(() => import('./pages/possibilities/DEIInclusionGrantScenario'));

// OFFERINGS PAGES
const Offerings = lazy(() => import('./pages/Offerings'));
const UBISocialPrograms = lazy(() => import('./pages/offerings/UBISocialPrograms'));
const DonationsCSR = lazy(() => import('./pages/offerings/DonationsCSR'));
const InvestmentsPEMA = lazy(() => import('./pages/offerings/InvestmentsPEMA'));
const CorporateEmployeeBenefits = lazy(() => import('./pages/offerings/CorporateEmployeeBenefits'));
const StartupFundraising = lazy(() => import('./pages/offerings/StartupFundraising'));

// APP DASHBOARD
const AppDashboard = lazy(() => import('./pages/AppDashboard'));

// Demo Pages (Public, Read-Only)
const DemoReceipts = lazy(() => import('./pages/demo/DemoReceipts'));

import GILNudge from './components/gil/GILNudge';

function AppRouter() {
  const location = useLocation();
  
  // P6 V2: Update document title based on route
  useDocumentTitle();
  
  // Observation Mode: lightweight anonymous telemetry
  useObservation();
  
  // Handle GIL explain action (connect to GIL drawer when available)
  const handleExplainWithGil = (prompt) => {
    // For now, log the prompt - will connect to GIL drawer
    console.log('GIL prompt requested:', prompt);
    // TODO: Open GIL drawer with pre-populated prompt
  };
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isHomepage = location.pathname === '/' || location.pathname === '/historical-timeline';
  const isEvidencePage = location.pathname.startsWith('/evidence/');
  const isMomentPage = location.pathname.startsWith('/moment/');
  const isPartnerPage = location.pathname.startsWith('/partner');
  const isWorkspacePage = location.pathname.startsWith('/workspace');
  const isAdminSetupPage = location.pathname.startsWith('/admin/setup');
  const isPublicNarrativePage = ['/product', '/platform', '/platform/granullo-pass', '/platform/payment-twins', '/use-cases', '/architecture', '/proof', '/launch-observability', '/simulate', '/trust', '/program-builder', '/live-programs', '/what-granullo-is', '/simulation-room', '/play', '/play/rail-partner', '/play/sponsor', '/play/investor', '/demand-pulse', '/demo-flow', '/ask-intelligence', '/data-fabric', '/pricing', '/playbook', '/signals', '/estimator', '/partners', '/partners/integrate'].includes(location.pathname) || location.pathname.startsWith('/gtm') || location.pathname.startsWith('/play') || location.pathname.startsWith('/simulations') || location.pathname.startsWith('/intelligence') || location.pathname.startsWith('/investor');
  const isSharePage = location.pathname.startsWith('/share/');
  const isDiligenceReviewPage = location.pathname.startsWith('/diligence-review/');
  const isCreatePage = location.pathname === '/create';
  // SHELL BOUNDARY: Canonical command surfaces — no legacy workspace chrome
  const isCanonicalSurface = ['/ops/observability', '/world-ready', '/crm', '/momentum'].includes(location.pathname) || location.pathname.startsWith('/ops/');
  // The SupPlant evaluator surfaces carry their own Granullo shell and their
  // own sandbox disclosure. No platform chrome may float over them.
  // Every /sandbox/* surface is an evaluator product, not a chapter of the
  // marketing narrative: founder chrome floats over its own controls and its
  // Home affordance leaves the sandbox entirely. The operator-side lab route
  // is the same product under a different path.
  const isSupplantSandbox = location.pathname.startsWith('/sandbox/supplant')
    || location.pathname.startsWith('/sandbox/policy-lab')
    || location.pathname.startsWith('/granullo/virtual-asset-lab');
  // The Farmer App is a BENEFICIARY surface. A farmer is not a founder and
  // must never meet founder chrome: no "Ask Granullo", no Back/Home pill, no
  // session recording. It also carries its own SIMULATED footer, which the
  // floating pills were covering.
  const isBeneficiarySurface = location.pathname.startsWith('/farmer')
    || location.pathname.startsWith('/my-entitlement');
  // The sign-in page is the first Granullo surface an external evaluator
  // sees; floating platform widgets overlapped its controls.
  const isLoginPage = location.pathname === '/login';
  const isCleanPage = isHomepage || isEvidencePage || isMomentPage || isPublicNarrativePage || isPartnerPage || isWorkspacePage || isAdminSetupPage || isSharePage || isDiligenceReviewPage || isCreatePage || isCanonicalSurface || isSupplantSandbox || isLoginPage || isBeneficiarySurface;

  return (
    <>
      <ScrollToTop />
      {!isCleanPage && <FounderNav />}
      {!isCleanPage && <ReviewModeWrapper />}
      <FirstVisitRedirect />
      <FounderModeOffset>
        {!isCleanPage && <DemoBadge />}
        {!isCleanPage && <DemoModeBadge />}
        {!isCleanPage && <SessionRecordingControls />}
        {!isCleanPage && <GILNudge onExplainWithGil={handleExplainWithGil} />}
        {/* DEPRECATED: ClarityOnIndicator - replaced by ExplainToggle in UniversalHeader */}
        {/* <ClarityOnIndicator /> */}
        {/* DEPRECATED: ClarityScrollNarrator - Clarity Mode superseded by Commitment model */}
        {/* <ClarityScrollNarrator /> */}
        <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--g-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 24, height: 24, border: '2px solid var(--g-border)', borderTopColor: 'var(--g-action-blue)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}>
        <Routes>
        {/* P6: First-Run Role Selection Interstitial */}
        <Route path="/start" element={<StartPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/proof-loop" element={<ProofLoopPage />} />
        <Route path="/intelligence" element={<IntelligencePage />} />
        <Route path="/intelligence/discover" element={<IntelligenceDiscoverChapter />} />
        <Route path="/intelligence/memory" element={<IntelligenceMemoryChapter />} />
        <Route path="/intelligence/momentum" element={<IntelligenceMomentumChapter />} />
        <Route path="/intelligence/world-models" element={<IntelligenceWorldModelsChapter />} />
        <Route path="/intelligence/how-it-thinks" element={<IntelligenceHowItThinksChapter />} />
        <Route path="/intelligence/capital-with-coordinates" element={<CapitalWithCoordinatesPage />} />
        <Route path="/intelligence/command-center" element={<CapitalRoutingCommandCenter />} />
        <Route path="/command-center" element={<CapitalRoutingCommandCenter />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/setup" element={<AdminSetup />} />
        <Route path="/register" element={<Register />} />
        <Route path="/recover-request" element={<RecoverRequest />} />
        <Route path="/recover/:token" element={<RecoverReset />} />
        <Route path="/setup-2fa" element={<TwoFASetup />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/granullo/demo" element={<GranulloDemoConsole />} />
        <Route path="/granullo/observatory" element={<DecisionObservatory />} />
        <Route path="/granullo/simulator" element={<CommitmentSimulator />} />
        <Route path="/granullo/zk-demo" element={<ZKCommitmentDemo />} />
        <Route path="/granullo/simulation/:simulationId" element={<SharedSimulationView />} />
        <Route path="/evidence/:hash" element={<EvidenceViewPage />} />
        <Route path="/moment/:shareId" element={<MomentPage />} />
        <Route path="/share/:linkId" element={<PublicShareView />} />
        <Route path="/partner" element={<PartnerPortal />} />
        <Route path="/partner/stripe" element={<StripeRailDemo />} />
        <Route path="/partner/stripe-result" element={<StripeRailDemo />} />
        <Route path="/granullo/failure-map" element={<GlobalFailureMap />} />
        <Route path="/granullo/oracle" element={<GranulloOracle />} />
        <Route path="/historical-timeline" element={<HistoricalTimelinePage />} />
        {/* PLAY — Decision Chain Game */}
        <Route path="/play" element={<PlayLanding />} />
        <Route path="/play/mission/:missionId" element={<PlayLanding />} />
        <Route path="/play/challenge/:challengeId" element={<PlayLanding />} />
        <Route path="/play/leaderboard" element={<PlayLeaderboard />} />
        <Route path="/play/rail-partner" element={<RailPartnerSimulation />} />
        <Route path="/play/sponsor" element={<SponsorSimulation />} />
        <Route path="/play/investor" element={<InvestorSimulation />} />
        <Route path="/play/regulator" element={<RegulatorSimulation />} />
        <Route path="/play/agentic-commerce" element={<AgenticCommerceSimulation />} />
        <Route path="/play/cbdc-benefits" element={<CBDCBenefitsSimulation />} />
        <Route path="/sandbox/:token" element={<PartnerSandboxWorkspace />} />
        <Route path="/diligence-review/:token" element={<DiligenceReviewPage />} />
        <Route path="/partner-sandbox" element={<ProtectedRoute><PartnerSandboxAdmin /></ProtectedRoute>} />

        {/* ═══ GRANULLO — Product Primitive Routes (Canonical) ═══ */}
        <Route path="/boundaries" element={<ProtectedRoute><V3BoundariesPage /></ProtectedRoute>} />
        <Route path="/triggers" element={<ProtectedRoute><V3TriggersPage /></ProtectedRoute>} />
        <Route path="/trusted-network" element={<ProtectedRoute><V3TrustedNetworkPage /></ProtectedRoute>} />
        <Route path="/twin-memory" element={<ProtectedRoute><V3TwinMemoryPage /></ProtectedRoute>} />
        <Route path="/compliance-passport" element={<ProtectedRoute><V3CompliancePassportPage /></ProtectedRoute>} />
        <Route path="/rail-readiness" element={<ProtectedRoute><V3RailReadinessPage /></ProtectedRoute>} />
        <Route path="/labs" element={<ProtectedRoute><V3LabsPage /></ProtectedRoute>} />
        {/* Version-retired redirects */}
        <Route path="/v3/boundaries" element={<Navigate to="/boundaries" replace />} />
        <Route path="/v3/triggers" element={<Navigate to="/triggers" replace />} />
        <Route path="/v3/trusted-network" element={<Navigate to="/trusted-network" replace />} />
        <Route path="/v3/twin-memory" element={<Navigate to="/twin-memory" replace />} />
        <Route path="/v3/compliance-passport" element={<Navigate to="/compliance-passport" replace />} />
        <Route path="/v3/rail-readiness" element={<Navigate to="/rail-readiness" replace />} />
        <Route path="/v3/labs" element={<Navigate to="/labs" replace />} />
        {/* ═══ CANONICAL CREATION SPINE — /create is the single entry point ═══ */}
        <Route path="/create" element={<ProtectedRoute><V3ProgramBuilder /></ProtectedRoute>} />
        {/* Legacy redirects → canonical spine (permanent, preserving query params) */}
        <Route path="/v3/create" element={<Navigate to="/create" replace />} />
        <Route path="/client/programs/new" element={<Navigate to="/create" replace />} />
        <Route path="/governance/programs/new" element={<Navigate to="/create" replace />} />
        <Route path="/programs/new" element={<Navigate to="/create" replace />} />

        {/* Legacy redirects: /simulations → /play */}
        <Route path="/simulations" element={<Navigate to="/play" replace />} />
        <Route path="/simulations/rail-partner" element={<Navigate to="/play/rail-partner" replace />} />
        <Route path="/simulations/sponsor" element={<Navigate to="/play/sponsor" replace />} />
        <Route path="/simulations/investor" element={<Navigate to="/play/investor" replace />} />
        {/* Legacy simulation routes */}
        <Route path="/evaluation" element={<SimulationsPage />} />
        {/* PHASE G1: Autonomous Conviction Gateway */}
        <Route path="/simulation-room" element={<SimulationRoomPage />} />
        <Route path="/demand-pulse" element={<DemandPulsePage />} />
        {/* P1: v2 GTM Demo Flow — 30-second institutional conviction chain */}
        <Route path="/demo-flow" element={<DemoFlowPage />} />
        {/* Future routes prepared for institutional surfaces */}
        <Route path="/policy-brief" element={<PolicyBriefPage />} />
        <Route path="/one-page" element={<OnePagePage />} />
        <Route path="/request-demo" element={<RequestDemoPage />} />
        <Route path="/csr/apply" element={<CSRPublicApply />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        
        {/* FOUNDER DIRECTIVE: Global Commitment Infrastructure - Public GCI Lookup */}
        <Route path="/gci/:gciId" element={<GCIPublicLookup />} />
        
        {/* FOUNDER DIRECTIVE: Full Commitment Registry Explorer */}
        <Route path="/registry" element={<RegistryPage />} />
        
        {/* P3: Governance Scenarios (Public, Read-Only) */}
        <Route path="/scenarios/insurance-claims" element={<InsuranceClaimsScenario />} />
        <Route path="/scenarios/employee-benefits" element={<EmployeeBenefitsScenario />} />
        <Route path="/scenarios/csr-grants" element={<CSRGrantsScenario />} />
        
        {/* DEMO SURFACES - Public, Read-Only (P1.5 Tour Step 4) */}
        <Route path="/demo/receipts" element={<DemoReceipts />} />
        
        {/* PUBLIC REGULATORY PAGES - NO LOGIN REQUIRED */}
        <Route path="/ubi-governance" element={<UBIGovernanceHub />} />
        <Route path="/regulator-start-here" element={<RegulatorStartHere />} />
        {/* /start-here now routes to StartHerePage (P5) - see line 280 */}
        <Route path="/regulators" element={<Navigate to="/regulator-start-here" replace />} />
        <Route path="/regulatory-readiness" element={<RegulatoryReadiness />} />
        <Route path="/how-granullo-works" element={<HowGranulloWorksCanonical />} />
        <Route path="/model" element={<Navigate to="/how-granullo-works" replace />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/architecture" element={<ArchitecturePage />} />
        <Route path="/help/integrations" element={<BankIntegrationGuide />} />
        <Route path="/help/communication" element={<CommunicationDoctrine />} />
        
        {/* PHASE 5: LIVE WORLD ENTRY — Narrative + Trust Pages */}
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/readiness-check" element={<ReadinessCheck />} />
        
        {/* PUBLIC TRUST PAGE — What Granullo Is / Is Not */}
        <Route path="/what-granullo-is" element={<WhatGranulloIs />} />
        
        {/* =================================================================
            VERIFIER LINK LANDING — Phase 1 (Minimal)
            PUBLIC ROUTE — No authentication required
            "Recorded events. Not interpretation."
            ================================================================= */}
        <Route path="/v/:token" element={<VerifierLinkLanding />} />
        
        {/* A4: PRODUCTION OPS — Admin-only diagnostic dashboard */}
        <Route path="/ops" element={<ProtectedRoute><OpsPage /></ProtectedRoute>} />
        
        {/* P1: OBSERVABILITY COMMAND CENTER — Production Hardening War Room */}
        <Route path="/ops/observability" element={<ProtectedRoute><ObservabilityDashboard /></ProtectedRoute>} />
        <Route path="/ops/replay-console" element={<ProtectedRoute><ReplayConsole /></ProtectedRoute>} />
        <Route path="/ops/replay-console/:transactionId" element={<ProtectedRoute><ReplayConsole /></ProtectedRoute>} />
        
        {/* D: ADMIN VERIFIER LINKS — Super admin link oversight */}
        <Route path="/admin/verifier-links" element={<ProtectedRoute><VerifierLinksAdmin /></ProtectedRoute>} />
        
        {/* BANK EXECUTION LEDGER — Economic orchestration visibility */}
        <Route path="/bank/execution-ledger" element={<ProtectedRoute><BankExecutionLedger /></ProtectedRoute>} />
        
        {/* B2: LP / EXTERNAL VERIFIER EXPERIENCE — Read-only deal room access */}
        <Route path="/verifier/deals" element={<ProtectedRoute><VerifierDeals /></ProtectedRoute>} />
        <Route path="/verifier/deals/:dealId" element={<ProtectedRoute><VerifierDealRoom /></ProtectedRoute>} />
        
        {/* P1-B: AUDIT LEDGER — Read-Only Truth Surface */}
        {/* "Granullo does not create a ledger. Granullo reveals the ledger that already exists." */}
        <Route path="/ledger" element={<ProtectedRoute><LedgerPage /></ProtectedRoute>} />
        <Route path="/verifier/ledger" element={<ProtectedRoute><VerifierLedgerPage /></ProtectedRoute>} />
        
        {/* C1: HIRING & PEOPLE GOVERNANCE — Post-hire commitment tracking */}
        <Route path="/hiring-governance" element={<ProtectedRoute><HiringGovernance /></ProtectedRoute>} />
        
        {/* B1 EXT: ORG-LEVEL GOVERNANCE SIGNALS — Aggregate signals across deals */}
        <Route path="/org/:orgId/governance-signals" element={<ProtectedRoute><OrgGovernanceSignals /></ProtectedRoute>} />
        
        {/* APP DASHBOARD - Work/Resume Entry Point for Signed-in Users */}
        <Route path="/app" element={<AppDashboard />} />
        
        {/* TRUST & POLICY - Public pages */}
        <Route path="/trust" element={<Trust />} />
        <Route path="/epistemic-policy" element={<EpistemicPolicy />} />
        <Route path="/invariants" element={<Invariants />} />
        
        {/* P1: INTENT RECORDS — Adoption Surfaces */}
        <Route path="/intent-records" element={<IntentRecordsPage />} />
        
        {/* P3: PRICING — Commit-based model ($25/commit) */}
        <Route path="/pricing" element={<PricingPage />} />
        
        {/* Phase 6: CAPACITY PLANNER — Estimate commits before program launch */}
        <Route path="/capacity-planner" element={<CommitCapacityPlanner />} />
        
        {/* ENTERPRISE — Institutional deployment & support */}
        <Route path="/enterprise" element={<EnterprisePage />} />
        
        {/* PRODUCT — What Granullo Does */}
        <Route path="/product" element={<ProductPage />} />
        
        {/* INTELLIGENCE V2 — Ask Granullo Intelligence */}
        <Route path="/ask-intelligence" element={<AskIntelligencePage />} />
        <Route path="/data-fabric" element={<DataFabricPage />} />
        <Route path="/playbook" element={<PlaybookPage />} />
        <Route path="/signals" element={<SignalsPage />} />
        <Route path="/estimator" element={<MisusePreventionEstimator />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/integrate" element={<PartnersIntegratePage />} />

        {/* GTM HANDOFF — Partner-Facing & Founder Playbook Surfaces */}
        <Route path="/gtm" element={<GTMHub />} />
        <Route path="/gtm/rail-narrative" element={<RailPartnerNarrative />} />
        <Route path="/gtm/one-pager" element={<PartnerOnePager />} />
        <Route path="/gtm/sponsor-bank" element={<SponsorBankLanding />} />
        <Route path="/gtm/rail-partner" element={<RailPartnerLanding />} />
        <Route path="/gtm/demo-choreography" element={<FounderDemoChoreography />} />
        <Route path="/gtm/objections" element={<ObjectionHandlingPage />} />
        <Route path="/gtm/case-studies" element={<CaseStudyScripts />} />
        <Route path="/gtm/why-proof" element={<WHYProofStoryboards />} />
        <Route path="/gtm/pilot-rollout" element={<PilotRolloutNarrative />} />
        <Route path="/gtm/expansion-roadmap" element={<ExpansionRoadmap />} />
        <Route path="/gtm/pilot-closing" element={<PilotClosingNarrative />} />
        <Route path="/gtm/deck-screenshots" element={<EnterpriseDeckScreenshots />} />
        <Route path="/gtm/sandbox-session" element={<SandboxSessionScript />} />
        <Route path="/gtm/pilot-dashboard" element={<PilotDashboard />} />
        <Route path="/gtm/ops-dashboard" element={<OpsDashboard />} />

        {/* GRANULLO CRM — Commercial Intelligence Dashboard */}
        <Route path="/crm" element={<ProtectedRoute><CRMDashboard /></ProtectedRoute>} />

        {/* GRANULLO MOMENTUM — Follow-Up Orchestration + Studio */}
        <Route path="/momentum" element={<ProtectedRoute><MomentumDashboard /></ProtectedRoute>} />
        <Route path="/world-ready" element={<ProtectedRoute><WorldReadyDashboard /></ProtectedRoute>} />

        {/* WORKSPACE — Authenticated Partner Surfaces */}
        <Route path="/workspace/pilot-control" element={<PilotControlRoom />} />
        <Route path="/workspace/ops" element={<OpsWorkspace />} />
        <Route path="/workspace/sandbox-activation" element={<SandboxActivation />} />

        {/* PLATFORM — How the System Works */}
        <Route path="/platform" element={<PlatformPage />} />
        {/* PHASE G: Platform IA Expansion — Granullo Pass & Payment Twins */}
        <Route path="/platform/granullo-pass" element={<PlatformGranulloPassPage />} />
        <Route path="/platform/payment-twins" element={<PlatformPaymentTwinsPage />} />
        
        {/* USE CASES — Sector-specific proof pages */}
        <Route path="/use-cases" element={<UseCasesPage />} />
        
        {/* PROOF — Trust Conversion Engine */}
        <Route path="/proof" element={<ProofPage />} />
        
        {/* LAUNCH OBSERVABILITY — Founder-grade dashboard */}
        <Route path="/launch-observability" element={<LaunchObservabilityPage />} />
        
        {/* SIMULATE — Experiential System Surface */}
        <Route path="/simulate" element={<SimulatePage />} />
        
        {/* GRANULLO 2.0 — Program Builder (single surface, no duplication) & Live Programs */}
        <Route path="/program-builder" element={<TryGranulloSandboxStandalone isStandalone />} />
        <Route path="/live-programs" element={<LiveProgramsPage />} />
        
        {/* Phase 1B: BILLING — Commit Wallet & Purchase */}
        <Route path="/billing/commits" element={<ProtectedRoute><BillingCommits /></ProtectedRoute>} />
        
        {/* Phase 7: ACCOUNT CENTER — Commit Wallet Integration */}
        <Route path="/account" element={<ProtectedRoute><AccountCenterPage /></ProtectedRoute>} />
        <Route path="/account-center" element={<AccountCenterPage />} />
        <Route path="/wallet" element={<AccountCenterPage />} />
        
        {/* Phase 8: COMMIT GRANT POOL — Donations & Applications */}
        <Route path="/grants" element={<CommitGrantPoolPage />} />
        <Route path="/grants/pool" element={<CommitGrantPoolPage />} />
        <Route path="/grants/apply" element={<CommitGrantPoolPage />} />
        <Route path="/commit-grants/contribute" element={<CommitGrantPoolPage />} />
        
        {/* Phase 9: INVESTOR CONVICTION ROOM — Private Auth + Living Conviction */}
        <Route path="/investor" element={<InvestorGate><InvestorIntelligenceRoom /></InvestorGate>} />
        <Route path="/investor/sandbox" element={<InvestorGate><InvestorSandboxNewPage /></InvestorGate>} />
        <Route path="/investor/ip-ledger" element={<InvestorGate><IPLedgerPage /></InvestorGate>} />
        <Route path="/investor/moat" element={<InvestorGate><MoatVisualizationPage /></InvestorGate>} />
        <Route path="/investor/financials" element={<InvestorGate><FinancialTheoremPage /></InvestorGate>} />
        <Route path="/investor/valuation" element={<InvestorGate><FinancialTheoremPage /></InvestorGate>} />
        <Route path="/investor/dcf" element={<InvestorGate><FinancialTheoremPage /></InvestorGate>} />
        <Route path="/investor/returns" element={<InvestorGate><ReturnSimulatorPage /></InvestorGate>} />
        <Route path="/investor/progress" element={<InvestorGate><InvestorProgressPage /></InvestorGate>} />
        <Route path="/investor/diligence-export" element={<InvestorGate><DiligenceExportPage /></InvestorGate>} />
        <Route path="/investor/diligence" element={<InvestorGate><InvestorIntelligenceRoom /></InvestorGate>} />
        {/* Conviction System V2 */}
        <Route path="/investor/truth-tracking" element={<InvestorGate><TruthTrackingPage /></InvestorGate>} />
        <Route path="/investor/valuation-lab" element={<InvestorGate><ValuationLabPage /></InvestorGate>} />
        <Route path="/investor/conviction-replay" element={<InvestorGate><ConvictionReplayPage /></InvestorGate>} />
        <Route path="/investor/board-observer" element={<InvestorGate><BoardObserverPage /></InvestorGate>} />
        <Route path="/investor-sandbox" element={<InvestorGate><InvestorSandboxNewPage /></InvestorGate>} />

        {/* Trust Room — Role-gated conviction surfaces */}
        <Route path="/trust-room/:role" element={<TrustRoomGate><TrustRoomOverviewPage /></TrustRoomGate>} />
        <Route path="/trust-room/:role/truth-tracking" element={<TrustRoomGate><TrustRoomTruthTrackingPage /></TrustRoomGate>} />
        <Route path="/trust-room/:role/valuation-lab" element={<TrustRoomGate><TrustRoomValuationLabPage /></TrustRoomGate>} />
        <Route path="/trust-room/:role/conviction-replay" element={<TrustRoomGate><TrustRoomConvictionReplayPage /></TrustRoomGate>} />
        <Route path="/trust-room/:role/board-observer" element={<TrustRoomGate><TrustRoomBoardObserverPage /></TrustRoomGate>} />
        <Route path="/sandbox" element={<InvestorGate><InvestorSandboxNewPage /></InvestorGate>} />
        
        {/* Phase 8.5-D: GOVERNANCE VAULT — Zero-Knowledge Document Storage */}
        <Route path="/vault" element={<ProtectedRoute><VaultDashboard /></ProtectedRoute>} />
        <Route path="/vault/:programId" element={<ProtectedRoute><VaultDashboard /></ProtectedRoute>} />
        
        {/* Phase A: ONBOARDING — Structural Engine */}
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingHub /></ProtectedRoute>} />
        
        {/* P4.4: PREPAREDNESS DELTA — Value visualization */}
        <Route path="/preparedness" element={<PreparednessPage />} />
        
        {/* P5: CBDC INFRASTRUCTURE — Institutional, explanatory */}
        <Route path="/cbdc" element={<CBDCPage />} />
        
        {/* P6.2: INVESTOR NARRATIVE — Public, read-only, comprehension page */}
        <Route path="/investors" element={<InvestorsPage />} />
        
        {/* LENS SYSTEM — Public landing pages for each perspective */}
        <Route path="/lens/:lensId" element={<LensPage />} />
        
        {/* COMMIT GRANTS POOL — Public status page (aggregates only) */}
        <Route path="/commit-grants/pool-status" element={<PoolStatus />} />
        
        {/* COMMIT GRANTS POOL — Authenticated pages */}
        <Route path="/commit-grants/apply" element={<ProtectedRoute><GrantApply /></ProtectedRoute>} />
        <Route path="/commit-grants/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
        <Route path="/commit-grants/contribute" element={<ProtectedRoute><PoolContribute /></ProtectedRoute>} />
        
        {/* PHASE D: STRUCTURAL TELEMETRY PANEL */}
        <Route path="/telemetry" element={<ProtectedRoute><TelemetryDashboard /></ProtectedRoute>} />
        
        {/* PHASE E: ONGOING AUDIT MODULE */}
        <Route path="/audit/timeline" element={<ProtectedRoute><AuditTimeline /></ProtectedRoute>} />
        <Route path="/audit/compliance" element={<ProtectedRoute><AuditCompliance /></ProtectedRoute>} />
        
        {/* PHASE Z: INVESTOR SIMULATION MODE — Moved to /simulation under UX Architecture */}
        {/* Route removed - now handled by SimulationEnvironmentPage */}
        
        {/* INSURANCE LAYER */}
        <Route path="/insurance/insurers" element={<ProtectedRoute><InsurersPage /></ProtectedRoute>} />
        <Route path="/insurance/disputes" element={<ProtectedRoute><DisputePackages /></ProtectedRoute>} />
        
        {/* P5: START HERE — Narrative entry point */}
        <Route path="/start-here" element={<StartHerePage />} />
        
        {/* P5: FOUNDER CONSOLE — Absolute oversight (Protected) */}
        <Route path="/founder" element={<ProtectedRoute><FounderConsolePage /></ProtectedRoute>} />
        <Route path="/founder/intelligence" element={<ProtectedRoute><FounderIntelligencePage /></ProtectedRoute>} />
        <Route path="/founder/intelligence/gtm" element={<ProtectedRoute><FounderGTMPage /></ProtectedRoute>} />
        <Route path="/founder/intelligence/friction" element={<ProtectedRoute><FounderFrictionPage /></ProtectedRoute>} />
        <Route path="/founder/intelligence/investor" element={<ProtectedRoute><FounderInvestorPage /></ProtectedRoute>} />
        <Route path="/founder/marketing" element={<ProtectedRoute><FounderMarketingPage /></ProtectedRoute>} />
        <Route path="/founder/links" element={<ProtectedRoute><FounderLinksPage /></ProtectedRoute>} />
        
        {/* CLIENT PRODUCTION CONTROL PLANES */}
        <Route path="/production" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/templates" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/policies" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/approvals" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/execution" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/overrides" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        <Route path="/production/promotions" element={<ProtectedRoute><ProductionControlPage /></ProtectedRoute>} />
        
        {/* BOARDROOM MIRROR ROUTES */}
        <Route path="/revenue-boardroom" element={<ProtectedRoute><RevenueBoardroomPage /></ProtectedRoute>} />
        <Route path="/valuation-boardroom" element={<ProtectedRoute><ValuationBoardroomPage /></ProtectedRoute>} />
        
        {/* PHASE 5: NETWORK INTAKE — Founder Review Console */}
        <Route path="/network-review" element={<ProtectedRoute><NetworkReviewPage /></ProtectedRoute>} />
        
        {/* PHASE 2: SANDBOX + VERIFIER + FOUNDER CONTROL PANEL */}
        {/* /verify/:token is PUBLIC — token-bound verification, no auth required */}
        <Route path="/verify/:token" element={<VerifyPage />} />
        {/* /sandbox routes require authentication */}
        <Route path="/sandbox" element={<ProtectedRoute><SandboxLanding /></ProtectedRoute>} />
        <Route path="/sandbox/scenario/:scenarioId" element={<ProtectedRoute><SandboxScenario /></ProtectedRoute>} />
        
        {/* BANK OPERATIONAL WORKFLOW LAYER — END-TO-END INSTITUTIONAL KYC */}
        <Route path="/bank/schema-builder" element={<ProtectedRoute><BankSchemaBuilder /></ProtectedRoute>} />
        <Route path="/apply/banks" element={<ProtectedRoute><SponsorApplyBanks /></ProtectedRoute>} />
        <Route path="/bank/review" element={<ProtectedRoute><BankReviewDashboard /></ProtectedRoute>} />
        <Route path="/packs/:packId" element={<ProtectedRoute><PackViewer /></ProtectedRoute>} />
        
        {/* PHASE 4: GLASS + ATLAS VIEW — Institutional Visibility (READ-ONLY) */}
        {/* ROUTE STANDARDIZATION: Redirect old /atlas to canonical /granullo-atlas */}
        <Route path="/atlas" element={<Navigate to="/granullo-atlas" replace />} />
        <Route path="/glass" element={<ProtectedRoute><GlassPage /></ProtectedRoute>} />
        
        {/* FOUNDER DIRECTIVE: Granullo Atlas Intelligence Interface */}
        <Route path="/granullo-atlas" element={<GranulloAtlasPage />} />
        
        {/* System Completion: New Engine Routes */}
        <Route path="/decision-fork" element={<DecisionForkPage />} />
        <Route path="/atlas-exposure" element={<AtlasExposurePage />} />
        <Route path="/glass-inspector" element={<GlassInspectorPage />} />
        <Route path="/glass-inspector/:caseId" element={<GlassInspectorPage />} />
        
        {/* PHASE 7: GRANULLO INTELLIGENCE SURFACE — Structural Intelligence UI */}
        {/* Public demo mode (no program selected) */}
        <Route path="/program-intelligence" element={<IntelligenceLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<IntelligenceOverview />} />
          <Route path="cirs" element={<IntelligenceCIRS />} />
          <Route path="dse" element={<IntelligenceDSE />} />
          <Route path="sfci" element={<IntelligenceSFCI />} />
          <Route path="iis" element={<IntelligenceIIS />} />
          <Route path="eml" element={<IntelligenceEML />} />
          <Route path="passport" element={<IntelligencePassport />} />
          <Route path="cid" element={<IntelligenceCID />} />
        </Route>
        {/* Program-specific intelligence (protected) */}
        <Route path="/program-intelligence/:programId" element={<ProtectedRoute><IntelligenceLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<IntelligenceOverview />} />
          <Route path="cirs" element={<IntelligenceCIRS />} />
          <Route path="dse" element={<IntelligenceDSE />} />
          <Route path="sfci" element={<IntelligenceSFCI />} />
          <Route path="iis" element={<IntelligenceIIS />} />
          <Route path="eml" element={<IntelligenceEML />} />
          <Route path="passport" element={<IntelligencePassport />} />
          <Route path="cid" element={<IntelligenceCID />} />
        </Route>
        
        {/* PHASE 7.1: CONSUMER SURFACE — Granullo Consumer (Public) */}
        <Route path="/consumer" element={<ConsumerLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ConsumerOverview />} />
          <Route path="map" element={<ConsumerMap />} />
          <Route path="timeline" element={<ConsumerTimeline />} />
          <Route path="passport" element={<ConsumerPassport />} />
          <Route path="about" element={<ConsumerAbout />} />
        </Route>
        
        {/* PHASE 2: IMPACT INTELLIGENCE LAYER — Control Room UI */}
        {/* Read-only telemetry from IOL Blocks 11 & 12 */}
        {/* Public route for investor demo; deterministic synthetic data */}
        <Route path="/control-room" element={<ControlRoomPage />} />
        
        {/* WAR ROOM MODE — Fullscreen decision command center */}
        <Route path="/war-room" element={<WarRoomPage />} />
        
        {/* PHASE D: GLOBAL EXPOSURE MAP — Commitment Intelligence Stack */}
        {/* Read-only spatial visualization derived from sealed Phase A/B/C artifacts */}
        <Route path="/exposure-map" element={<ProtectedRoute><ExposureMapPage /></ProtectedRoute>} />
        
        {/* PHASE 3: NETWORK ENTRY LAYER */}
        {/* Public routes for ecosystem entry and sandbox demo */}
        <Route path="/ecosystem" element={<EcosystemPage />} />
        <Route path="/test-drive" element={<SandboxPage />} />
        <Route path="/join/program-creators" element={<ProgramCreatorsJoinPage />} />
        <Route path="/join/banks" element={<BanksJoinPage />} />
        <Route path="/join/merchants" element={<MerchantsJoinPage />} />
        
        {/* PHASE 6.11: Granullo Security Toolkit */}
        <Route path="/security-toolkit" element={<SecurityToolkitPage />} />
        
        {/* PHASE 4: DEMAND MARKETPLACE EXPERIENCE (UI) */}
        {/* Read-only telemetry from IOL Blocks 10, 11 & 12 */}
        {/* Public route for marketplace demo; deterministic synthetic data */}
        <Route path="/marketplace" element={<MarketplacePage />} />
        
        {/* PHASE F: UX ARCHITECTURE — New Surfaces */}
        {/* Observatory: Read-only network visualization */}
        <Route path="/observatory" element={<ObservatoryPage />} />
        {/* Simulation: Network scenario modeling with presets and insight panel */}
        <Route path="/simulation" element={<NetworkSimulationPage />} />
        {/* Mission Control: Operational dashboard */}
        <Route path="/mission-control" element={<MissionControlPage />} />
        {/* Investor Demo: Guided narrative walkthrough */}
        <Route path="/investor-demo" element={<InvestorDemoPage />} />
        
        {/* GRANULLO PHASE 2: Authority Matrix */}
        <Route path="/authority" element={<AuthorityMatrixPage />} />
        
        {/* DEV SETTINGS - Available without login in dev mode */}
        <Route path="/dev-settings" element={<DevSettings />} />
        <Route path="/design-qa" element={<DesignSystemQA />} />
        <Route path="/internal-docs" element={<InternalDocs />} />
        <Route path="/navigation-audit" element={<NavigationAudit />} />
        
        {/* GRANULLO LABS - Internal Experimentation Plane (ISOLATED) */}
        <Route path="/labs" element={<Labs />} />
        <Route path="/labs/scenario-studio" element={<ScenarioStudio />} />
        <Route path="/labs/interactive-maps" element={<InteractiveMaps />} />
        <Route path="/labs/governance-dashboard" element={<GovernanceDashboard />} />
        
        {/* POST-SIGNUP ACTIVATION - Feature-flagged guided experience */}
        <Route path="/activate" element={<PostSignupActivation />} />
        
        {/* EXTERNAL SOURCE DASHBOARD - Phase 7 External Evidence Ingestion */}
        <Route path="/external-sources" element={<ExternalSourceDashboard />} />
        
        {/* GOVERNANCE MIRROR - Phase 8.2 Governed vs Evidenced */}
        <Route path="/governance-mirror" element={<GovernanceMirror />} />
        <Route path="/governance-mirror/:programId" element={<GovernanceMirror />} />
        
        {/* REGULATOR CO-PILOT - Phase 8.3 Timeline Replay & Evidence Walkthrough */}
        <Route path="/regulator-copilot" element={<ProtectedRoute><RegulatorCopilot /></ProtectedRoute>} />
        
        {/* MERCHANT GOVERNANCE CONSOLE - P1 Consolidated */}
        <Route path="/bank/merchant-governance" element={<ProtectedRoute><MerchantConsole /></ProtectedRoute>} />
        <Route path="/regulator/merchant-governance" element={<ProtectedRoute><MerchantConsole /></ProtectedRoute>} />
        <Route path="/auditor/merchant-governance" element={<ProtectedRoute><MerchantConsole /></ProtectedRoute>} />
        
        {/* DEPRECATED ROUTES - Redirects to consolidated console (backwards compatibility) */}
        <Route path="/merchant-discovery" element={<Navigate to="/bank/merchant-governance?tab=visibility" replace />} />
        <Route path="/bank/merchant-requests" element={<Navigate to="/bank/merchant-governance?tab=requests" replace />} />
        <Route path="/merchant-console" element={<Navigate to="/bank/merchant-governance" replace />} />
        
        {/* SPEND VISIBILITY - Phase 12 */}
        <Route path="/spend-visibility" element={<ProtectedRoute><SpendVisibility /></ProtectedRoute>} />
        
        {/* RECEIPTS SYSTEM - P0 */}
        <Route path="/receipts" element={<ProtectedRoute><Receipts /></ProtectedRoute>} />
        
        {/* CFO VAULT - P0 */}
        <Route path="/cfo-vault" element={<ProtectedRoute><CFOVault /></ProtectedRoute>} />
        
        {/* P6: QUANTUM SAFE VAULT */}
        <Route path="/qsv" element={<ProtectedRoute><QSVPage tenantId="demo" programId="default" userId="demo-user" /></ProtectedRoute>} />
        
        {/* OFFERINGS - "WHAT GRANULLO IS FOR" (PUBLIC, BUYER-ORIENTED) */}
        <Route path="/offerings" element={<Offerings />} />
        <Route path="/offerings/ubi-social-programs" element={<UBISocialPrograms />} />
        <Route path="/offerings/donations-csr" element={<DonationsCSR />} />
        <Route path="/offerings/investments-pe-ma" element={<InvestmentsPEMA />} />
        <Route path="/offerings/corporate-employee-benefits" element={<CorporateEmployeeBenefits />} />
        <Route path="/offerings/startup-fundraising" element={<StartupFundraising />} />
        
        {/* POSSIBILITIES - PUBLIC SCENARIO WALKTHROUGHS */}
        <Route path="/possibilities" element={<Possibilities />} />
        <Route path="/possibilities/government-tenders-roadworks" element={<GovernmentTendersScenario />} />
        <Route path="/possibilities/esg-linked-budgets" element={<ESGLinkedBudgetsScenario />} />
        <Route path="/possibilities/climate-finance" element={<ClimateFinanceScenario />} />
        <Route path="/possibilities/solar-farm-installations" element={<SolarFarmScenario />} />
        <Route path="/possibilities/carbon-credits-environmental" element={<CarbonCreditsScenario />} />
        <Route path="/possibilities/public-private-funds" element={<PublicPrivateFundsScenario />} />
        <Route path="/possibilities/scholarship-fellowship-governance" element={<ScholarshipScenario />} />
        <Route path="/possibilities/universal-income-programs" element={<UniversalIncomeScenario />} />
        
        {/* CORPORATE PROGRAM SCENARIOS - PUBLIC */}
        <Route path="/possibilities/corporate/learning-and-development" element={<LearningAndDevelopmentScenario />} />
        <Route path="/possibilities/corporate/mental-health-allowance" element={<MentalHealthAllowanceScenario />} />
        <Route path="/possibilities/corporate/remote-work-stipend" element={<RemoteWorkStipendScenario />} />
        <Route path="/possibilities/corporate/dei-inclusion-grant" element={<DEIInclusionGrantScenario />} />
        
        <Route path="/demo-landing" element={<ProtectedRoute><DemoLanding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/organizations" element={<ProtectedRoute><Organizations /></ProtectedRoute>} />
        
        {/* Enterprise Deal Infrastructure */}
        <Route path="/deals" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
        <Route path="/deals/new" element={<ProtectedRoute><CreateDealPage /></ProtectedRoute>} />
        {/* PHASE 2: DealRoom replaces DealDetailPage — Consequence Environment */}
        <Route path="/deals/:dealId" element={<ProtectedRoute><DealRoom /></ProtectedRoute>} />
        <Route path="/deals/:dealId/commitments/new" element={<ProtectedRoute><CreateCommitmentPage /></ProtectedRoute>} />
        <Route path="/deals/:dealId/commitments/:commitmentId" element={<ProtectedRoute><CommitmentDetailPage /></ProtectedRoute>} />
        
        {/* Design System Check (dev verification) */}
        <Route path="/design-check" element={<DesignSystemCheck />} />
        
        <Route path="/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
        {/* /programs/new redirected to /create — see canonical spine block above */}
        <Route path="/programs/:id" element={<ProtectedRoute><ProgramDetail /></ProtectedRoute>} />
        <Route path="/programs/:id/execution-room" element={<ProtectedRoute><ExecutionRoom /></ProtectedRoute>} />
        <Route path="/programs/:id/readiness" element={<ProtectedRoute><ProgramReadiness /></ProtectedRoute>} />
        <Route path="/programs/:id/conversations" element={<ProtectedRoute><ProgramConversations /></ProtectedRoute>} />
        <Route path="/programs/:id/auditor-view" element={<ProtectedRoute><AuditorView /></ProtectedRoute>} />
        
        {/* GRANULLO PLATFORM EVOLUTION: Phase 2 — Program Structure Dashboard */}
        <Route path="/program/structure" element={<ProtectedRoute><ProgramStructurePage /></ProtectedRoute>} />
        <Route path="/programs/:id/structure" element={<ProtectedRoute><ProgramStructurePage /></ProtectedRoute>} />
        {/* FOUNDER DIRECTIVE: Public demo route for Phase 2 showcase */}
        <Route path="/demo/structure" element={<ProgramStructurePage />} />
        
        <Route path="/execution-room/:programId" element={<ProtectedRoute><ExecutionRoom /></ProtectedRoute>} />
        <Route path="/governance-history" element={<ProtectedRoute><GovernanceHistory /></ProtectedRoute>} />
        <Route path="/governance-intelligence" element={<ProtectedRoute><GovernanceIntelligence /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/dpp-simulator" element={<ProtectedRoute><DPPSimulator /></ProtectedRoute>} />
        <Route path="/policies" element={<ProtectedRoute><Policies /></ProtectedRoute>} />
        <Route path="/policies/simulator" element={<ProtectedRoute><PolicySimulator /></ProtectedRoute>} />
        <Route path="/integrate" element={<ProtectedRoute><IntegrateGranulloPage /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><IntegrationsHubPage /></ProtectedRoute>} />
        <Route path="/integrations/:integrationId" element={<ProtectedRoute><IntegrationDetailPage /></ProtectedRoute>} />
        {/* Canonical SupPlant sandbox — single external URL: /sandbox/supplant */}
        <Route path="/sandbox/supplant" element={<ProtectedRoute><SupPlantCanonicalEntry /></ProtectedRoute>} />
        <Route path="/sandbox/supplant/control" element={<ProtectedRoute><SupplantSandboxPage /></ProtectedRoute>} />
        <Route path="/sandbox/supplant/embedded" element={<ProtectedRoute><SupplantEmbeddedPage /></ProtectedRoute>} />
        <Route path="/programs/console" element={<ProtectedRoute><SupplantProgramConsole /></ProtectedRoute>} />
        <Route path="/granullo/factory" element={<ProtectedRoute><FactoryConsole /></ProtectedRoute>} />
        <Route path="/granullo/virtual-asset-lab" element={<ProtectedRoute><VirtualAssetLab /></ProtectedRoute>} />
        <Route path="/sandbox/policy-lab" element={<ProtectedRoute><VirtualAssetLab /></ProtectedRoute>} />
        <Route path="/farmer" element={<ProtectedRoute><FarmerAppPage /></ProtectedRoute>} />
        {/* The beneficiary's own entry point. Not sponsor-protected: a farmer
            signs in with their phone and reaches only their own entitlement. */}
        <Route path="/my-entitlement" element={<MyEntitlement />} />

        {/* Phase 3 — Granullo Core canonical inspection surface */}
        <Route path="/granullo/core" element={<GranulloCoreInspector />} />
        <Route path="/granullo/core/*" element={<GranulloCoreInspector />} />
        <Route path="/granullo/execution" element={<GranulloExecutionConsole />} />
        <Route path="/granullo/execution/*" element={<GranulloExecutionConsole />} />
        <Route path="/policies/:id" element={<ProtectedRoute><PolicyDetail /></ProtectedRoute>} />
        <Route path="/policies/:id/programmability" element={<ProtectedRoute><AdvancedProgrammability /></ProtectedRoute>} />
        <Route path="/beneficiaries" element={<ProtectedRoute><Beneficiaries /></ProtectedRoute>} />
        <Route path="/beneficiaries/:id" element={<ProtectedRoute><BeneficiaryDetail /></ProtectedRoute>} />
        <Route path="/merchants" element={<ProtectedRoute><Merchants /></ProtectedRoute>} />
        <Route path="/merchants/:id" element={<ProtectedRoute><MerchantDetail /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/transactions/:id" element={<ProtectedRoute><TransactionDetail /></ProtectedRoute>} />
        <Route path="/approvals" element={<ProtectedRoute><Approvals /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/audit" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
        <Route path="/compliance" element={<ProtectedRoute><ComplianceDashboard /></ProtectedRoute>} />
        <Route path="/compliance-alerts" element={<ProtectedRoute><ComplianceAlerts /></ProtectedRoute>} />
        <Route path="/bank/escrow" element={<ProtectedRoute><EscrowConsole /></ProtectedRoute>} />
        <Route path="/bank/card-applications" element={<ProtectedRoute><BankCardApplications /></ProtectedRoute>} />
        <Route path="/bank/onboarding" element={<ProtectedRoute><BankOnboarding /></ProtectedRoute>} />
        
        {/* Bank-First Experience Routes */}
        <Route path="/bank" element={<ProtectedRoute><BankHome /></ProtectedRoute>} />
        <Route path="/bank/start-here" element={<ProtectedRoute><BankStartHere /></ProtectedRoute>} />
        <Route path="/bank/merchants" element={<ProtectedRoute><MerchantControlCenter /></ProtectedRoute>} />
        <Route path="/bank/integrations" element={<ProtectedRoute><BankIntegrations /></ProtectedRoute>} />
        <Route path="/bank/agreements" element={<ProtectedRoute><BankAgreements /></ProtectedRoute>} />
        
        {/* Client-First Experience Routes */}
        <Route path="/client" element={<ProtectedRoute><ClientHome /></ProtectedRoute>} />
        <Route path="/client/start-here" element={<ProtectedRoute><ClientStartHere /></ProtectedRoute>} />
        <Route path="/client/programs" element={<ProtectedRoute><ClientPrograms /></ProtectedRoute>} />
        {/* /client/programs/new redirected to /create — see canonical spine block */}
        <Route path="/client/reports" element={<ProtectedRoute><ClientReports /></ProtectedRoute>} />
        <Route path="/client/reports/:archetype" element={<ProtectedRoute><ClientReportDashboard /></ProtectedRoute>} />
        
        <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
        <Route path="/guides" element={<ProtectedRoute><Guides /></ProtectedRoute>} />
        <Route path="/guides/:role" element={<ProtectedRoute><GuideDetail /></ProtectedRoute>} />
        <Route path="/guides/playbook/:guideId" element={<ProtectedRoute><PlaybookDetail /></ProtectedRoute>} />
        
        {/* How Granullo Works - Step-by-Step Walkthroughs */}
        <Route path="/help/how-granullo-works" element={<ProtectedRoute><HowGranulloWorks /></ProtectedRoute>} />
        <Route path="/help/how-granullo-works/:walkthroughId" element={<ProtectedRoute><WalkthroughDetail /></ProtectedRoute>} />
        
        {/* How Programs Are Set Up - Explanatory Walkthroughs */}
        <Route path="/help/program-walkthroughs" element={<ProtectedRoute><ProgramWalkthroughs /></ProtectedRoute>} />
        <Route path="/help/program-walkthroughs/:walkthroughId" element={<ProtectedRoute><ProgramWalkthroughDetail /></ProtectedRoute>} />
        
        {/* Enterprise Confidence & Learning Hub */}
        <Route path="/help/granullo-in-5-sentences" element={<ProtectedRoute><GranulloIn5Sentences /></ProtectedRoute>} />
        <Route path="/help/learning-hub" element={<ProtectedRoute><LearningHub /></ProtectedRoute>} />
        <Route path="/help/ask-granullo" element={<ProtectedRoute><AskGranullo /></ProtectedRoute>} />
        {/* /help/communication is PUBLIC - defined above with regulatory pages */}
        
        {/* Market Readiness — /onboarding is canonical at line ~1010 (ProtectedRoute<OnboardingHub />) */}
        <Route path="/why-granullo" element={<ProtectedRoute><WhyGranullo /></ProtectedRoute>} />
        
        {/* Canonical Visual Model - /how-granullo-works is PUBLIC (defined above) */}
        <Route path="/visual-flow" element={<ProtectedRoute><GranulloVisualFlow /></ProtectedRoute>} />
        
        {/* Trust Layer - Regulator View */}
        <Route path="/trust-layer" element={<ProtectedRoute><TrustLayer /></ProtectedRoute>} />
        
        {/* Trust & Oversight - Top Level Routes */}
        <Route path="/trust-pilot" element={<ProtectedRoute><TrustPilot /></ProtectedRoute>} />
        <Route path="/executive-dashboard" element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>} />
        
        {/* Trust & Assurance - Educational Content (All Users) */}
        <Route path="/help/trust-assurance/what-is" element={<ProtectedRoute><WhatIsTrustLayer /></ProtectedRoute>} />
        <Route path="/help/trust-assurance/what-we-prove" element={<ProtectedRoute><WhatWeProve /></ProtectedRoute>} />
        <Route path="/help/trust-assurance/why-it-matters" element={<ProtectedRoute><WhyItMatters /></ProtectedRoute>} />
        <Route path="/help/trust-assurance/what-it-is-not" element={<ProtectedRoute><WhatItIsNot /></ProtectedRoute>} />
        
        {/* Trust Layer Example Walkthroughs (Educational) */}
        <Route path="/help/trust-assurance/examples" element={<ProtectedRoute><TrustLayerExamples /></ProtectedRoute>} />
        <Route path="/help/trust-assurance/examples/identity-assurance" element={<ProtectedRoute><IdentityAssuranceExample /></ProtectedRoute>} />
        <Route path="/help/trust-assurance/examples/policy-compliance" element={<ProtectedRoute><PolicyComplianceExample /></ProtectedRoute>} />
        
        <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
        <Route path="/access-requests" element={<ProtectedRoute><AccessRequests /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><TemplateManagement /></ProtectedRoute>} />
        <Route path="/expiration" element={<ProtectedRoute><ExpirationTracking /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationProfile /></ProtectedRoute>} />
        <Route path="/settings/api" element={<ProtectedRoute><DeveloperSettings /></ProtectedRoute>} />
        <Route path="/settings/org" element={<ProtectedRoute><OrgSettings /></ProtectedRoute>} />
        <Route path="/developers" element={<ProtectedRoute><DeveloperPortal /></ProtectedRoute>} />
        <Route path="/developers/api-keys" element={<ProtectedRoute><DeveloperSettings defaultTab="api-keys" /></ProtectedRoute>} />
        <Route path="/developers/webhooks" element={<ProtectedRoute><DeveloperSettings defaultTab="webhooks" /></ProtectedRoute>} />
        <Route path="/developers/apps/:id" element={<ProtectedRoute><IntegrationAppDetails /></ProtectedRoute>} />
        
        {/* Ops Dashboard - removed duplicate, using OpsPage at line 384 */}
        
        {/* Governance - Bank Change Requests */}
        <Route path="/settings/governance/bank-requests" element={<ProtectedRoute><BankRequests /></ProtectedRoute>} />
        <Route path="/settings/governance/bank-requests/new" element={<ProtectedRoute><NewBankRequest /></ProtectedRoute>} />
        <Route path="/settings/governance/bank-requests/:bcrId" element={<ProtectedRoute><BankRequestDetail /></ProtectedRoute>} />
        
        {/* Review & Clarifications */}
        <Route path="/review-clarifications" element={<ProtectedRoute><ReviewClarifications /></ProtectedRoute>} />
        <Route path="/review-clarifications/:threadId" element={<ProtectedRoute><ReviewThreadDetail /></ProtectedRoute>} />
        
        {/* Campaign Brand Vault */}
        <Route path="/campaign-brand-vault" element={<ProtectedRoute><CampaignBrandVault /></ProtectedRoute>} />
        
        {/* Settings - Integrations (Enterprise API Keys + Webhooks) */}
        <Route path="/settings/integrations" element={<ProtectedRoute><SettingsIntegrations /></ProtectedRoute>} />
        <Route path="/settings/integrations/api-keys" element={<ProtectedRoute><SettingsIntegrations defaultTab="api-keys" /></ProtectedRoute>} />
        <Route path="/settings/integrations/webhooks" element={<ProtectedRoute><SettingsIntegrations defaultTab="webhooks" /></ProtectedRoute>} />
        <Route path="/settings/integrations/webhook-retries" element={<ProtectedRoute><WebhookRetries /></ProtectedRoute>} />
        
        {/* CSR Hub - Entry Landing Surface */}
        <Route path="/csr/start" element={<ProtectedRoute><CSRHub /></ProtectedRoute>} />
        <Route path="/csr/hub" element={<Navigate to="/csr/start" replace />} />
        
        {/* CSR Execution - Consolidated Allocations/Communication/Merchants */}
        <Route path="/csr/execution" element={<ProtectedRoute><CSRExecution /></ProtectedRoute>} />
        
        {/* CSR Insights - Consolidated Analytics/Intelligence/Notes */}
        <Route path="/csr/insights" element={<ProtectedRoute><CSRInsights /></ProtectedRoute>} />
        
        {/* CSR Partners (Foundations & Charities) */}
        <Route path="/csr/partners" element={<ProtectedRoute><CSRPartners /></ProtectedRoute>} />
        <Route path="/csr/partners/applications" element={<ProtectedRoute><CSRPartners defaultTab="applications" /></ProtectedRoute>} />
        <Route path="/csr/allocations" element={<ProtectedRoute><CSRAllocations /></ProtectedRoute>} />
        <Route path="/csr/programs" element={<ProtectedRoute><CSRPrograms /></ProtectedRoute>} />
        <Route path="/csr/allocations/:allocationId/communication" element={<ProtectedRoute><CSRCommunication /></ProtectedRoute>} />
        <Route path="/csr/allocations/:allocationId/merchants" element={<ProtectedRoute><CSRMerchantGovernance /></ProtectedRoute>} />
        <Route path="/csr/analytics" element={<ProtectedRoute><CSRAnalytics /></ProtectedRoute>} />
        
        {/* CSR Foundation Invites - Corporate sends, Foundation accepts */}
        <Route path="/csr/invite/:token" element={<CSRInviteAccept />} />
        <Route path="/csr/invites" element={<Navigate to="/csr/partners?tab=invites" replace />} />
        <Route path="/csr/communication" element={<ProtectedRoute><CSRThreadedCommunication /></ProtectedRoute>} />
        <Route path="/csr/communication/:threadId" element={<ProtectedRoute><CSRThreadDetail /></ProtectedRoute>} />
        
        {/* Governance Map - "Why CSR Breaks" */}
        <Route path="/governance/why-csr-breaks" element={<ProtectedRoute><CSRGovernanceMap /></ProtectedRoute>} />
        
        {/* FOUNDER DIRECTIVE: Organization Commitment Dashboard */}
        <Route path="/organization/commitments" element={<OrganizationCommitmentDashboard />} />
        
        {/* Phase 11.4 — Program Lifecycle Governance UI */}
        <Route path="/governance/programs" element={<ProtectedRoute><GovernanceProgramsPage /></ProtectedRoute>} />
        {/* /governance/programs/new redirected to /create — see canonical spine block */}
        <Route path="/governance/programs/:programId" element={<ProtectedRoute><GovernanceProgramDetailPage /></ProtectedRoute>} />
        
        {/* Impact Intelligence - Read-Only Context Module */}
        <Route path="/impact-intelligence" element={<ProtectedRoute><ImpactIntelligence /></ProtectedRoute>} />

        {/* Programs · World Pilot Readiness Packs (public — investor-/buyer-facing) */}
        <Route path="/programs/pilots" element={<PilotReadinessPage />} />
        <Route path="/pilots" element={<Navigate to="/programs/pilots" replace />} />

        {/* Programs · Live Needs Discovery (public structured matching, internal priors view) */}
        <Route path="/programs/needs-discovery" element={<NeedsDiscoveryPage />} />
        <Route path="/needs" element={<Navigate to="/programs/needs-discovery" replace />} />
        <Route path="/needs-discovery" element={<Navigate to="/programs/needs-discovery" replace />} />

        {/* Programs · Granullo Foundation Network (public trust directory) */}
        <Route path="/foundations" element={<FoundationNetworkPage />} />
        <Route path="/programs/foundations" element={<Navigate to="/foundations" replace />} />

        {/* Programs · Governed Merchant Network (public, where money is allowed to move) */}
        <Route path="/programs/merchant-network" element={<MerchantNetworkPage />} />
        <Route path="/programs/merchant-network/:merchantId" element={<MerchantDetailPage />} />
        <Route path="/merchant-network" element={<Navigate to="/programs/merchant-network" replace />} />

        {/* Programs · Dedicated pack detail pages (each pillar gets its own URL) */}
        <Route path="/programs/csr-foundations"    element={<PilotPackDetailPage />} />
        <Route path="/programs/trade-corridor"     element={<PilotPackDetailPage />} />
        <Route path="/programs/enterprise-finance" element={<PilotPackDetailPage />} />
        <Route path="/programs/pos-misuse"         element={<PilotPackDetailPage />} />

        {/* Internal · TSD Engine Live Regression Dashboard (admin/super_admin/tech) */}
        <Route path="/intelligence/tsd-tests" element={<DiscoverTestDashboard />} />
        
        {/* Using Granullo - In-Product Help Hub */}
        <Route path="/using-granullo" element={<ProtectedRoute><UsingGranullo /></ProtectedRoute>} />
        
        {/* Bank Integration Guide - /help/integrations is PUBLIC (defined above) */}
        
        {/* These regulatory routes are now PUBLIC (defined at top of Routes) */}
        {/* /regulatory-readiness, /start-here, /regulator-start-here, /regulators */}
        
        {/* Glossary - /glossary is PUBLIC (defined above) */}
        
        {/* Institutional Explainability Views */}
        <Route path="/regulatory-walkthrough" element={<ProtectedRoute><RegulatoryWalkthrough /></ProtectedRoute>} />
        <Route path="/audit-narrative" element={<ProtectedRoute><AuditNarrative /></ProtectedRoute>} />
        <Route path="/audit-narrative/:programId" element={<ProtectedRoute><AuditNarrative /></ProtectedRoute>} />
        
        {/* P2.0 Intent Records UI */}
        <Route path="/intents" element={<ProtectedRoute><IntentListPage /></ProtectedRoute>} />
        <Route path="/intents/:intentId" element={<ProtectedRoute><IntentDetailPage /></ProtectedRoute>} />
        
        {/* Developer Tools */}
        <Route path="/dev/hover-gallery" element={<ProtectedRoute><HoverGallery /></ProtectedRoute>} />
        
        <Route path="/request-access" element={<RequestAccess />} />
        <Route path="/deploy" element={<DeployPage />} />
        
        {/* P5: 404 Catch-all with proper navigation */}
        <Route path="*" element={<ScopedNotFound />} />
      </Routes>
      </Suspense>
      <WayfindingRail />
      </FounderModeOffset>
    </>
  );
}

// Hides children on cinematic/clean pages to ensure immersive experience
function HideOnHomepage({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  // Surface Purity: Hide floating overlays on ALL public-facing surfaces.
  // Only show on authenticated internal routes.
  const publicSurfacePrefixes = ['/evidence/', '/moment/', '/partner', '/workspace', '/share/', '/consumer', '/v/', '/verify/', '/sandbox/', '/diligence-review/'];
  const publicExactPaths = [
    '/', '/historical-timeline', '/product', '/platform', '/platform/granullo-pass',
    '/platform/payment-twins', '/architecture', '/proof', '/launch-observability', '/simulate', '/trust',
    '/simulation-room', '/play', '/play/rail-partner', '/play/sponsor', '/play/investor', '/play/regulator', '/play/agentic-commerce', '/play/cbdc-benefits', '/simulations', '/simulations/rail-partner', '/simulations/sponsor', '/simulations/investor', '/demand-pulse', '/demo-flow', '/pricing', '/enterprise',
    '/investors', '/ecosystem', '/labs', '/start', '/start-here', '/what-granullo-is',
    '/how-granullo-works', '/glossary', '/regulatory-readiness', '/regulator-start-here',
    '/login', '/register', '/recover-request', '/privacy', '/terms', '/cbdc',
    '/preparedness', '/case-studies', '/readiness-check', '/possibilities',
    '/offerings', '/marketplace', '/observatory', '/simulation', '/investor-demo',
    '/control-room', '/intent-records', '/demo', '/request-demo', '/one-page',
    '/policy-brief', '/granullo-atlas', '/decision-fork', '/atlas-exposure',
    '/ubi-governance', '/activate', '/design-check', '/dev-settings', '/design-qa',
    '/internal-docs', '/navigation-audit',
    '/ask-intelligence',
    '/data-fabric',
    '/playbook', '/signals', '/estimator', '/partners',
    '/intelligence', '/use-cases',
  ];
  const isPublicSurface =
    publicExactPaths.includes(location.pathname) ||
    publicSurfacePrefixes.some(p => location.pathname.startsWith(p)) ||
    location.pathname.startsWith('/scenarios/') ||
    location.pathname.startsWith('/possibilities/') ||
    location.pathname.startsWith('/offerings/') ||
    location.pathname.startsWith('/lens/') ||
    location.pathname.startsWith('/labs/') ||
    location.pathname.startsWith('/demo/') ||
    location.pathname.startsWith('/intelligence') ||
    location.pathname.startsWith('/join/') ||
    location.pathname.startsWith('/csr/') ||
    location.pathname.startsWith('/gci/') ||
    location.pathname.startsWith('/granullo/') ||
    location.pathname.startsWith('/play/') ||
    location.pathname.startsWith('/help/');
  // SHELL BOUNDARY: Canonical command surfaces — suppress legacy workspace chrome
  const isCanonicalSurface =
    ['/ops/observability', '/world-ready', '/crm', '/momentum'].includes(location.pathname) ||
    location.pathname.startsWith('/ops/');
  // BENEFICIARY SURFACE: the Farmer App belongs to the farmer, not to the
  // founder. Sponsor chrome (the intelligence dock, overlays, provenance)
  // must not appear over it, and the floating pills were covering its own
  // SIMULATED footer.
  const isBeneficiarySurface = location.pathname.startsWith('/farmer')
    || location.pathname.startsWith('/my-entitlement');
  // Also suppress if user is not authenticated (public visitor)
  if (isPublicSurface || isCanonicalSurface || isBeneficiarySurface || !user) return null;
  return children;
}

/**
 * HideOnHomepageExceptPlay — Same as HideOnHomepage but allows Play routes.
 * Intelligence dock should be available on /play/* for stakeholder interop.
 */
function HideOnHomepageExceptPlay({ children }) {
  const location = useLocation();
  const isPlayRoute = location.pathname.startsWith('/play/');
  if (isPlayRoute) return children;
  return <HideOnHomepage>{children}</HideOnHomepage>;
}

/**
 * ShowOnlyPreLogin — Inverse of HideOnHomepage.
 * Renders children ONLY when the user is unauthenticated (pre-login surfaces).
 * Used for the LensOrb so it appears across every public/marketing screen
 * but is fully hidden once the user signs in.
 */
function ShowOnlyPreLogin({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user) return null;
  // The sign-in page is the evaluator's entry to the product, not a
  // marketing screen: the floating orb overlapped its controls.
  if (pathname === '/login' || pathname.startsWith('/sandbox/')) return null;
  // A farmer is logged out by sponsor standards and is not a visitor to be
  // marketed at: the orb covered their own sign-out control.
  if (pathname.startsWith('/my-entitlement')
      || pathname.startsWith('/farmer')) return null;
  return children;
}


// First Login Wrapper Component
function FirstLoginWrapper({ children }) {
  const { showFirstLogin, acknowledgeFirstLogin } = useAuth();
  
  return (
    <>
      {children}
      <FirstLoginModal 
        isOpen={showFirstLogin} 
        onAcknowledge={acknowledgeFirstLogin} 
      />
    </>
  );
}

/**
 * DeepSystemProviders — Heavy infrastructure providers for non-homepage routes.
 * These 19 providers are only mounted when the user navigates away from the homepage,
 * preventing Heap OOM on initial page load.
 */
function DeepSystemProviders({ children }) {
  return (
    <GranulloGlassProvider>
    <SimulationHistoryProvider>
    <ExposureEngineProvider>
    <CBDCSimulationProvider>
    <AuthorityMatrixProvider>
    <ProgrammablePolicyProvider>
    <ProgramIntegrityProvider>
    <SystemBlueprintProvider>
    <BaselineSignalsProvider>
    <MonitoringEngineProvider>
    <CascadeEngineProvider>
    <SystemStressProvider>
    <DecisionMatrixProvider>
    <ScenarioEngineProvider>
    <InterventionComparisonProvider>
    <SystemMemoryProvider>
    <VerifiedBlueprintProvider>
    <CommitEconomyProvider>
      {children}
    </CommitEconomyProvider>
    </VerifiedBlueprintProvider>
    </SystemMemoryProvider>
    </InterventionComparisonProvider>
    </ScenarioEngineProvider>
    </DecisionMatrixProvider>
    </SystemStressProvider>
    </CascadeEngineProvider>
    </MonitoringEngineProvider>
    </BaselineSignalsProvider>
    </SystemBlueprintProvider>
    </ProgramIntegrityProvider>
    </ProgrammablePolicyProvider>
    </AuthorityMatrixProvider>
    </CBDCSimulationProvider>
    </ExposureEngineProvider>
    </SimulationHistoryProvider>
    </GranulloGlassProvider>
  );
}

/**
 * RouteAwareProviders — Conditionally loads heavy infrastructure providers.
 * Homepage and historical-timeline get lightweight rendering (no heavy providers).
 * All other routes get the full deep system provider stack.
 */
function RouteAwareProviders({ children }) {
  const location = useLocation();

  // Partner Workspace — completely isolated render tree
  if (location.pathname.startsWith('/workspace')) {
    return (
      <PartnerWorkspaceProvider>
        <Routes>
          <Route path="/workspace/login" element={<PartnerLogin />} />
          <Route path="/workspace/register" element={<PartnerRegister />} />
          <Route path="/workspace/*" element={<WorkspaceShell />} />
        </Routes>
      </PartnerWorkspaceProvider>
    );
  }

  const lightRoutes = ['/', '/historical-timeline', '/product', '/platform', '/platform/granullo-pass', '/platform/payment-twins', '/use-cases', '/architecture', '/proof', '/launch-observability', '/simulate', '/trust', '/simulation-room', '/play', '/play/rail-partner', '/play/sponsor', '/play/investor', '/simulations', '/simulations/rail-partner', '/simulations/sponsor', '/simulations/investor', '/demand-pulse', '/demo-flow', '/ask-intelligence', '/data-fabric', '/pricing', '/playbook', '/signals', '/estimator', '/partners', '/intelligence'];
  const isLightRoute = lightRoutes.includes(location.pathname) || location.pathname.startsWith('/evidence/') || location.pathname.startsWith('/moment/') || location.pathname.startsWith('/partner') || location.pathname.startsWith('/workspace') || location.pathname.startsWith('/share/') || location.pathname.startsWith('/gtm') || location.pathname.startsWith('/play') || location.pathname.startsWith('/diligence-review/') || location.pathname.startsWith('/investor');

  if (isLightRoute) {
    return children;
  }

  return (
    <DeepSystemProviders>
      {children}
    </DeepSystemProviders>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FounderProvider>
            <LensProvider>
              <GranulloLensV2Provider>
              <RoleProvider>
                <ClarityProvider>
                  <ClarityModeProvider>
                    <ClarityV2Provider>
                      <ExplainProvider>
                        <HotkeyProvider>
                          <UndoProvider>
                            <DemoProvider>
                              <DemoLinkProvider>
                                <DemoModeProvider>
                                  <InstitutionalModeProvider>
                                  <RouteAwareProviders>
                                    <GuidedViewWrapper>
                                    <FirstLoginWrapper>
                                      {/* FOUNDER DIRECTIVE: Global System Status Bar — hidden on homepage AND post-login (Attio doctrine). */}
                                      <ShowOnlyPreLogin><HideOnHomepage><GlobalSystemStatusBar /></HideOnHomepage></ShowOnlyPreLogin>
                                      <ExplainStyles />
                                      <AppRouter />
                                      <ProofJourneyBar />
                                      <CommandPalette />
                                      <ClarityPrompt />
                                      <HideOnHomepage><TokenProvenanceOverlay /></HideOnHomepage>
                                    </FirstLoginWrapper>
                                    <Toaster position="top-right" />
                                    <SessionWarning />
                                    {/* Global overlays — hidden on homepage for fullscreen hero */}
                                    <HideOnHomepage>
                                    <DemoOverlay />
                                    <GranulloGlassOverlay />
                                    </HideOnHomepage>
                                    <HideOnHomepageExceptPlay>
                                    <AskGranulloDock />
                                    </HideOnHomepageExceptPlay>
                                    {/* LENS V2: Universal Inspection Orb — pre-login only (public surfaces) */}
                                    <ShowOnlyPreLogin>
                                      <LensOrb />
                                      <LensPanel />
                                    </ShowOnlyPreLogin>
                                    </GuidedViewWrapper>
                                  </RouteAwareProviders>
                                  </InstitutionalModeProvider>
                                </DemoModeProvider>
                              </DemoLinkProvider>
                            </DemoProvider>
                          </UndoProvider>
                        </HotkeyProvider>
                      </ExplainProvider>
                    </ClarityV2Provider>
                  </ClarityModeProvider>
                </ClarityProvider>
              </RoleProvider>
              </GranulloLensV2Provider>
            </LensProvider>
          </FounderProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
